// src/components/Dashboard.js
import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Swal from "sweetalert2";
import { auth, db, secondaryAuth } from "./firebase";
import "../App.css";
import { signOut, onAuthStateChanged, createUserWithEmailAndPassword } from "firebase/auth";

import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { useNavigate, useLocation } from "react-router-dom";
import { onMessage, messaging } from "../components/firebase";
import { initializeFCMToken } from "../utils/fcmToken";
import { useNotification } from "../contexts/NotificationContext";
import { clearAppBadge, setAppBadge } from "../utils/badge";
import { isMobileDevice as checkMobileDevice } from "../utils/deviceDetection";
import AttendanceModal from "./AttendanceModal";
import EmbeddedBoardView from "./EmbeddedBoardView";
import EmbeddedChatView from "./EmbeddedChatView";
import AttendanceCalendar from "./AttendanceCalendar";
import { calculateLeaveBalance } from "../utils/attendanceHelper";
import AdminLeaves from "../pages/admin/Leaves";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  // Redirect /view/ routes to dashboard
  useEffect(() => {
    if (location.pathname.startsWith('/view/')) {
      // Extract projectId and projectName from URL
      const pathParts = location.pathname.split('/');
      const projectId = pathParts[2];
      const projectName = decodeURIComponent(pathParts[3] || '');

      // Preserve query parameters (like taskId)
      const searchParams = new URLSearchParams(location.search);
      const queryString = searchParams.toString();
      const redirectUrl = queryString ? `/dashboard?${queryString}&projectId=${projectId}` : `/dashboard?projectId=${projectId}`;

      // Redirect to dashboard and select the project
      navigate(redirectUrl, { replace: true });

      // Set the project as selected after a brief delay to ensure dashboard is mounted
      setTimeout(() => {
        if (projectId) {
          setSelectedProjectId(projectId);
          setSelectedProjectName(projectName);
          setActivePage('dashboard');
          setProjectViewMode('chat');
        }
      }, 100);
    }
  }, [location.pathname, location.search, navigate]);


  // Extract current projectId from location if user is on ViewTaskPage (for notification checks)
  const currentProjectId = location.pathname.startsWith('/view/')
    ? location.pathname.split('/')[2]
    : null;

  // AUTH STATES
  const [userEmail, setUserEmail] = useState("");
  const [userUID, setUserUID] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userCreatedAt, setUserCreatedAt] = useState(null);
  const [userName, setUserName] = useState("");
  const [authChecked, setAuthChecked] = useState(false); // Track if auth check is complete
  const firstLetter = userEmail ? userEmail.charAt(0).toUpperCase() : "";

  // PROJECT STATES
  const [projectName, setProjectName] = useState("");
  const [projects, setProjects] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [isMobile, setIsMobile] = useState(false); // Check if mobile/tablet screen (max-width: 992px)
  const [isMobileDevice, setIsMobileDevice] = useState(false); // Check if actual mobile device (not just screen size)
  const [isExtraSmall, setIsExtraSmall] = useState(false); // Check if extra small screen (max-width: 400px)
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar visibility

  // ASSIGN MODAL
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);

  // ADD USER MODAL
  const [showUserModal, setShowUserModal] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [newUserInput, setNewUserInput] = useState(""); // For WhatsApp-style input

  // UI page control
  const [activePage, setActivePage] = useState("dashboard");

  // PROJECT VIEW STATE - For showing project content (Chat/Board) within dashboard
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState("");
  const [projectViewMode, setProjectViewMode] = useState("chat"); // "chat" or "board"
  const [selectedBoardUser, setSelectedBoardUser] = useState("Unassigned"); // Default to Unassigned (or All)

  // Date range filter state - default to empty (show all tasks)
  const getTodayDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };
  const [boardFromDate, setBoardFromDate] = useState(""); // Empty = show all
  const [boardToDate, setBoardToDate] = useState(""); // Empty = show all

  // ATTENDANCE MODAL
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);

  // NOTIFICATIONS STATE
  const [projectNotificationCounts, setProjectNotificationCounts] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [projectMentionCounts, setProjectMentionCounts] = useState({}); // Track mention notifications per project
  const [notificationsMuted, setNotificationsMuted] = useState(() => {
    // Load from localStorage, default to false
    const saved = localStorage.getItem("notificationsMuted");
    return saved === "true";
  });

  // PWA INSTALL PROMPT STATE
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  // ADMIN TASKS STATE - Real-time task list for admin
  // eslint-disable-next-line no-unused-vars
  const [adminTasks, setAdminTasks] = useState([]);


  // ATTENDANCE STATE
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [adminSelectedUserLeaves, setAdminSelectedUserLeaves] = useState([]);
  const [filterDate, setFilterDate] = useState(() => {
    // Default to today's date for admin users
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [filterEmail, setFilterEmail] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceViewMode, setAttendanceViewMode] = useState("table");
  const [attendanceCalendarMonth, setAttendanceCalendarMonth] = useState(new Date());
  const [regularizationInputs, setRegularizationInputs] = useState({}); // { recordId: { show: bool, reason: string } }
  const [submittingRegularization, setSubmittingRegularization] = useState(null); // recordId being submitted
  const [regularizationModal, setRegularizationModal] = useState(null); // { recordId, userName, reason, date }
  const [holidays, setHolidays] = useState([]); // Array of holiday objects

  // LEAVES STATE
  const [myLeaves, setMyLeaves] = useState([]);
  const [leaveType, setLeaveType] = useState("Casual");
  const [leaveFromDate, setLeaveFromDate] = useState("");
  const [leaveToDate, setLeaveToDate] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [submittingLeave, setSubmittingLeave] = useState(false);

  // Check if mobile/tablet screen (max-width: 992px) and extra small (max-width: 400px)
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 992;
      const extraSmall = window.innerWidth <= 400;
      setIsMobile(mobile);
      setIsExtraSmall(extraSmall);
      // Close sidebar when switching to desktop
      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if actual mobile device (not just screen size)
  useEffect(() => {
    setIsMobileDevice(checkMobileDevice());
  }, []);

  // PWA Install Prompt Handler
  useEffect(() => {
    // Check if already installed first
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInStandaloneMode = window.navigator.standalone || isStandalone;

    if (isInStandaloneMode) {
      console.log('✅ App is already installed (standalone mode)');
      setShowInstallButton(false);
      return;
    }

    // Check if deferredPrompt already exists (from index.html)
    if (window.deferredPrompt) {
      console.log('📱 Found existing deferredPrompt from index.html');
      setDeferredPrompt(window.deferredPrompt);
      setShowInstallButton(true);
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('📱 beforeinstallprompt event received');
      e.preventDefault();
      setDeferredPrompt(e);
      window.deferredPrompt = e; // Store globally
      setShowInstallButton(true);
      console.log('✅ Install button should now be visible');
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('✅ PWA installed successfully');
      setDeferredPrompt(null);
      window.deferredPrompt = null;
      setShowInstallButton(false);
    };

    // Listen for custom events from index.html
    const handlePWAInstallable = (e) => {
      console.log('📱 pwa-installable custom event received');
      if (e.detail) {
        handleBeforeInstallPrompt(e.detail);
      }
    };

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('pwa-installable', handlePWAInstallable);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwa-installed', handleAppInstalled);

    // Periodic check for deferredPrompt (in case event was missed)
    const checkInterval = setInterval(() => {
      if (window.deferredPrompt && !deferredPrompt) {
        console.log('📱 Found deferredPrompt on periodic check');
        setDeferredPrompt(window.deferredPrompt);
        setShowInstallButton(true);
      }
    }, 2000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwa-installable', handlePWAInstallable);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwa-installed', handleAppInstalled);
      clearInterval(checkInterval);
    };
  }, [deferredPrompt]);

  // Handle install button click
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Try to get from window object (fallback)
      const prompt = window.deferredPrompt;
      if (!prompt) {
        console.warn('⚠️ Install prompt not available');
        return;
      }

      prompt.prompt();
      const { outcome } = await prompt.userChoice;

      if (outcome === 'accepted') {
        console.log('✅ User accepted install prompt');
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'App installed successfully!',
          timer: 3000
        });
      } else {
        console.log('❌ User dismissed install prompt');
      }

      window.deferredPrompt = null;
      setShowInstallButton(false);
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('✅ User accepted install prompt');
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'App installed successfully!',
        timer: 3000
      });
    } else {
      console.log('❌ User dismissed install prompt');
    }

    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  // AUTH LISTENER
  useEffect(() => {
    let isMounted = true;
    let hasAuthenticated = false;
    let navigationTimeout = null;
    let authStateRestored = false; // Track if auth state has been restored from persistence
    let intentionalLogout = false; // Track if logout was intentional (via logout button)

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Check if logout was intentional (set by handleLogout)
      if (window.__intentionalLogout) {
        intentionalLogout = true;
        window.__intentionalLogout = false; // Reset
      }

      // First check: Wait for auth state to be restored from persistence
      // This prevents false logouts when auth state is being restored
      if (!authStateRestored) {
        authStateRestored = true;
        // Give Firebase time to restore auth state from localStorage
        await new Promise(resolve => setTimeout(resolve, 500));
        // Re-check user after waiting
        const currentUser = auth.currentUser;
        if (!currentUser && !user) {
          // Auth state restored and no user found - this is a real logout
          if (isMounted) {
            setAuthChecked(true);
            // Only navigate if we're on a protected route
            if (location.pathname !== "/") {
              console.log("🔄 No authenticated user found, navigating to login...");
              navigate("/");
            }
          }
          return;
        }
        // If user exists after restoration, continue with normal flow
        if (currentUser && !user) {
          // Auth was restored, user exists
          user = currentUser;
        }
      }

      if (!user) {
        // Clear any pending navigation
        if (navigationTimeout) {
          clearTimeout(navigationTimeout);
          navigationTimeout = null;
        }

        // Mark auth check as complete
        if (isMounted) {
          setAuthChecked(true);
        }

        // Only navigate to login if logout was intentional (via logout button)
        // Don't navigate on back button press or accidental navigation
        if (intentionalLogout && isMounted) {
          console.log("🔄 Intentional logout, navigating to login...");
          navigate("/");
          intentionalLogout = false; // Reset flag
        } else if (authStateRestored && location.pathname !== "/" && !hasAuthenticated) {
          // Only navigate if auth state restored, no user found, and user was never authenticated
          // This handles initial load when user is not logged in
          console.log("🔄 No authenticated user on initial load, navigating to login...");
          navigate("/");
        } else {
          // User pressed back button or navigation happened - don't logout
          // Keep user on current page, auth state will be restored
          console.log("⏸️ Preventing navigation - back button or accidental navigation detected");
        }
        return;
      }

      // Mark that we've authenticated at least once
      hasAuthenticated = true;

      if (!isMounted) return;

      // Wrap all async operations in try-catch to prevent errors from causing navigation issues
      try {
        setUserEmail(user.email || "");
        setUserUID(user.uid || "");
        setAuthChecked(true); // Mark auth check as complete
        console.log("👤 User authenticated:", {
          email: user.email,
          uid: user.uid
        });

        // Ensure user document exists in Firestore before initializing FCM
        try {
          const ref = doc(db, "users", user.uid);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            setUserRole(snap.data().role || "user");
            setUserCreatedAt(snap.data().createdAt);
            setUserName(snap.data().username || user.email?.split("@")[0] || "User");
            console.log("✅ User document exists in Firestore");
          } else {
            // Create user document if it doesn't exist
            console.log("📝 User document not found, creating...");
            setUserRole("user");
            await setDoc(ref, {
              email: user.email || "",
              username: user.email?.split("@")[0] || "User",
              role: "user",
              createdAt: serverTimestamp(),
            }, { merge: true });
            console.log("✅ User document created in Firestore");
          }
        } catch (err) {
          console.error("❌ Error fetching/creating user doc:", err);
          setUserRole("user");
          // Try to create user document even if fetch failed
          try {
            const ref = doc(db, "users", user.uid);
            await setDoc(ref, {
              email: user.email || "",
              username: user.email?.split("@")[0] || "User",
              role: "user",
              createdAt: serverTimestamp(),
            }, { merge: true });
            console.log("✅ User document created (fallback)");
          } catch (createErr) {
            console.error("❌ Failed to create user document:", createErr);
          }
        }

        // Initialize FCM token for push notifications
        // Enhanced initialization with multiple retries for new users
        // IMPORTANT: This runs for ALL users (admin and regular users)
        if (user.uid) {
          console.log("🔄 User logged in, initializing FCM token...");
          console.log("   User UID:", user.uid);
          console.log("   User Email:", user.email);
          console.log("   User Role:", userRole || "user (will be set)");

          // Wait for page to be fully loaded and service worker to register
          const initializeFCM = async () => {
            // Wait for DOM to be ready
            if (document.readyState !== 'complete') {
              await new Promise(resolve => {
                if (document.readyState === 'complete') {
                  resolve();
                } else {
                  window.addEventListener('load', resolve);
                }
              });
            }

            // Additional delay to ensure service worker registration from index.html completes
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Check notification permission first and prompt if needed
            if (Notification.permission === "default") {
              console.log("📢 Notification permission not set, will request...");
              // Permission will be requested in getFCMToken()
            } else if (Notification.permission === "denied") {
              console.warn("⚠️ Notification permission is DENIED");
              // Show prominent message to user
              Swal.fire({
                icon: "warning",
                title: "Notifications Blocked",
                html: `
                <p>You need to enable notifications to receive push alerts.</p>
                <p><strong>Steps to enable:</strong></p>
                <ol style="text-align: left; margin: 20px;">
                  <li>Click the <strong>🔒 lock icon</strong> in your browser's address bar</li>
                  <li>Go to <strong>Site settings</strong></li>
                  <li>Find <strong>Notifications</strong> and set it to <strong>Allow</strong></li>
                  <li>Refresh this page (F5)</li>
                </ol>
                <p>Or click the button below to try again:</p>
              `,
                showCancelButton: true,
                confirmButtonText: "Try Again",
                cancelButtonText: "Later",
                confirmButtonColor: "#2a8c7b"
              }).then((result) => {
                if (result.isConfirmed) {
                  // Try to request permission again (might work in some browsers)
                  Notification.requestPermission().then(permission => {
                    if (permission === "granted") {
                      console.log("✅ Permission granted, initializing FCM token...");
                      initializeFCMToken(user.uid);
                    } else {
                      console.warn("⚠️ Permission still not granted");
                    }
                  });
                }
              });
              return; // Don't proceed if permission is denied
            }

            // Initialize FCM token with retry mechanism
            const initializeWithRetry = async (retries = 5, delay = 3000) => {
              for (let attempt = 1; attempt <= retries; attempt++) {
                try {
                  console.log(`\n🔄 FCM Token Initialization - Attempt ${attempt}/${retries}`);
                  console.log("   Waiting before attempt...");

                  // Progressive delay between attempts
                  if (attempt > 1) {
                    await new Promise(resolve => setTimeout(resolve, delay * (attempt - 1)));
                  }

                  const success = await initializeFCMToken(user.uid);
                  if (success) {
                    console.log("✅ FCM token initialized successfully!");
                    // No toast - silent initialization
                    return true;
                  } else {
                    console.warn(`⚠️ Attempt ${attempt} failed, will retry...`);
                    if (attempt < retries) {
                      console.log(`   Next retry in ${delay * attempt / 1000} seconds...`);
                    }
                  }
                } catch (error) {
                  console.error(`❌ Attempt ${attempt} error:`, error.message);
                  if (attempt === retries) {
                    console.error("❌ FCM token initialization failed after all retries");
                    console.warn("💡 Troubleshooting:");
                    console.warn("   1. Check browser console for detailed errors");
                    console.warn("   2. Run: window.checkFCMStatus() to diagnose");
                    console.warn("   3. Run: window.registerFCMToken() to manually register");

                    // Show user-friendly message with action button
                    Swal.fire({
                      icon: "info",
                      title: "Enable Notifications",
                      html: `
                      <p>To receive push notifications, please:</p>
                      <ol style="text-align: left; margin: 20px;">
                        <li>Allow notification permission when prompted</li>
                        <li>Or click the lock icon → Site settings → Notifications → Allow</li>
                        <li>Then click the button below to register</li>
                      </ol>
                    `,
                      showCancelButton: true,
                      confirmButtonText: "Register Now",
                      cancelButtonText: "Later",
                      confirmButtonColor: "#2a8c7b"
                    }).then((result) => {
                      if (result.isConfirmed) {
                        // Import and call manual registration
                        import("../utils/fcmToken").then(({ registerFCMTokenManually }) => {
                          registerFCMTokenManually();
                        });
                      }
                    });
                  }
                }
              }
              return false;
            };

            await initializeWithRetry();
          };

          // Start initialization (don't await - run in background)
          initializeFCM().catch((err) => {
            console.error("❌ FCM initialization error:", err);
            // Don't navigate on FCM errors - just log them
          });
        }
      } catch (error) {
        // Catch any unhandled errors in the auth callback
        console.error("❌ Error in auth callback:", error);
        // Don't navigate on errors - user should stay on dashboard
        if (isMounted) {
          setAuthChecked(true);
        }
      }
    });

    // Handle back button - prevent login screen, allow app to close
    const handlePopState = (event) => {
      const currentUser = auth.currentUser;

      if (currentUser) {
        // User is authenticated
        if (location.pathname === "/" || location.pathname === "/login") {
          // Back button tried to go to login - prevent and redirect to dashboard
          console.log("🚫 Back button prevented - user is authenticated, redirecting to dashboard");
          event.preventDefault();
          navigate("/dashboard", { replace: true });
        }
        // If on dashboard, allow back button to work normally (may close app)
        // Don't prevent default - let browser handle it
      }
    };

    // Also handle browser back/forward buttons
    window.addEventListener("popstate", handlePopState);

    return () => {
      isMounted = false;
      if (navigationTimeout) {
        clearTimeout(navigationTimeout);
      }
      window.removeEventListener("popstate", handlePopState);
      unsubscribe();
    };
  }, [navigate, location.pathname, userRole]);

  // Handle foreground FCM messages (when app is open)
  useEffect(() => {
    if (!messaging || !onMessage || !userRole) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      const addedBy = payload.data?.addedByName || payload.data?.addedBy || payload.data?.createdBy || "Unknown";
      const taskName = payload.data?.taskName || "New Task";

      showNotification({
        title: "New Task Added",
        body: `${addedBy} added new task: ${taskName}`,
        projectId: payload.data?.projectId || "",
        projectName: payload.data?.projectName || "",
        taskId: payload.data?.taskId || "",
        taskName: taskName,
        addedBy: payload.data?.addedBy || payload.data?.createdBy || "",
        addedByName: addedBy,
        link: payload.data?.link || "",
        timestamp: payload.data?.timestamp || Date.now(),
      });

      // Note: Badge increment is handled by Firestore listener (onSnapshot)
      // This prevents duplicate badge increments when both FCM and Firestore detect the same task
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [showNotification, userRole]);

  // Listen for service worker messages (background notifications)
  useEffect(() => {
    if (!navigator.serviceWorker) return;

    const handleMessage = (event) => {
      console.log("[Dashboard] 📨 Message from service worker:", event.data);

      if (event.data?.type === "INC_BADGE") {
        // Don't increment badge here - badge count is calculated from unread notifications
        // The notification count listener will update badge automatically
        console.log("[Dashboard] Background notification received - badge will update from unread count");

        // Also show notification popup if app is open
        if (event.data.payload) {
          const payload = event.data.payload;
          const addedBy = payload.addedByName || payload.addedBy || "Unknown";
          const taskName = payload.taskName || "New Task";

          showNotification({
            title: "New Task Added",
            body: `${addedBy} added new task: ${taskName}`,
            projectId: payload.projectId || "",
            projectName: payload.projectName || "",
            taskId: payload.taskId || "",
            taskName: taskName,
            addedBy: payload.addedBy || "",
            addedByName: addedBy,
            link: payload.link || "",
            timestamp: payload.timestamp || Date.now(),
          });
        }
      } else if (event.data?.type === "CLEAR_BADGE") {
        // Clear badge when notification is clicked
        clearAppBadge().catch((error) => {
          console.error("[Dashboard] Error clearing badge from SW message:", error);
        });
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [showNotification]);

  // Initialize badge on app launch (only once, not on every focus)
  useEffect(() => {
    // Initialize badge on mount (only clears if app is already visible)
    // Don't clear on every focus - only on initial load
    const initBadge = async () => {
      try {
        // Check if Badge API is supported
        const isSupported = 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
        console.log("[Dashboard] 🔍 Badge API Check:");
        console.log("[Dashboard]   - setAppBadge available:", 'setAppBadge' in navigator);
        console.log("[Dashboard]   - clearAppBadge available:", 'clearAppBadge' in navigator);
        console.log("[Dashboard]   - Badge API supported:", isSupported);
        console.log("[Dashboard]   - User Agent:", navigator.userAgent);
        console.log("[Dashboard]   - Platform:", navigator.platform);

        if (isSupported) {
          // Use initializeBadge from utils which syncs with IndexedDB
          const { initializeBadge } = await import("../utils/badge");
          await initializeBadge();
          console.log("[Dashboard] ✅ Badge initialized using initializeBadge()");
        } else {
          console.log("[Dashboard] ⚠️ Badge API not supported in this browser");
          console.log("[Dashboard] ℹ️ Badge will not show on app icon");
          console.log("[Dashboard] ℹ️ This is normal for some browsers/devices");
        }
      } catch (error) {
        console.error("[Dashboard] ❌ Error initializing badge:", error);
        console.error("[Dashboard] Error details:", error.message);
      }
    };

    // Small delay to ensure app is fully loaded
    setTimeout(() => {
      initBadge();
    }, 500);
  }, []); // Only run once on mount

  // Clear badge only when user clicks notification (handled in NotificationPopup)
  // Don't clear on focus/visibility change - let badge persist


  // LOAD PROJECTS (realtime)
  useEffect(() => {
    if (!userUID) {
      console.log("⚠️ userUID not set, waiting...");
      return;
    }

    console.log("📋 Loading projects for user:", userUID);
    console.log("   User Email:", userEmail);
    console.log("   User Role:", userRole);

    let q;
    if (userRole === "admin") {
      // Admin sees ALL projects, ordered by newest first
      // Note: This requires a single-field index on createdAt (usually auto-created)
      q = query(collection(db, "projects"), orderBy("createdAt", "desc"));
    } else {
      // Regular users see only assigned projects
      q = query(collection(db, "projects"), where("users", "array-contains", userUID));
    }

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        console.log(`\n✅ Found ${list.length} projects`);

        // Detailed logging
        if (list.length === 0) {
          console.warn("⚠️ No projects found");
          if (userRole !== "admin") {
            console.warn("   User UID:", userUID);
            console.warn("   Make sure user is assigned to at least one project");
          }
        } else {
          list.forEach((p, index) => {
            // Only log first 5 projects to avoid console spam
            if (index < 5) {
              console.log(`\n   Project ${index + 1}: ${p.name} (ID: ${p.id})`);
              const isUserInArray = (p.users || []).includes(userUID);
              console.log(`     Current user in array:`, isUserInArray ? "✅ YES" : "❌ NO");

              if (!isUserInArray && userRole !== "admin") {
                console.error(`     ❌ ERROR: User ${userUID} is NOT in project's users array!`);
              }
            }
          });
        }

        setProjects(list);
      },
      (error) => {
        console.error("❌ Error loading projects:", error);
        console.error("   Error code:", error.code);
        console.error("   Error message:", error.message);
        if (userRole === "admin" && error.code === "failed-precondition") {
          console.error("   This might be a missing index for Admin query (orderBy createdAt)");
        }
      }
    );

    return () => unsub();
  }, [userUID, userEmail, userRole]);

  // Handle query parameters when landing on dashboard (after projects are loaded)
  useEffect(() => {
    if (location.pathname === '/dashboard' && projects.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const projectIdFromQuery = searchParams.get('projectId');
      const taskIdFromQuery = searchParams.get('taskId');

      if (projectIdFromQuery && !selectedProjectId) {
        // Find project name from projects list
        const project = projects.find(p => p.id === projectIdFromQuery);
        if (project) {
          setSelectedProjectId(projectIdFromQuery);
          setSelectedProjectName(project.name);
          setActivePage('dashboard');
          setProjectViewMode('chat');

          // If taskId is provided, highlight it (you may need to implement this)
          if (taskIdFromQuery) {
            // Store taskId for highlighting - you can implement this feature later
            console.log('Task ID to highlight:', taskIdFromQuery);
          }
        }
      }
    }
  }, [location.pathname, location.search, projects, selectedProjectId]);

  // REALTIME TASK LISTENER - Show popup when new task added
  useEffect(() => {
    if (!userUID || projects.length === 0) return;

    const unsubscribers = [];
    const lastTaskIds = {};
    const componentMountTime = Date.now(); // Track when component mounted
    const isFirstSnapshot = {}; // Track first snapshot per project

    // Initialize lastTaskIds with tasks that user has already seen
    const initializeSeenTasks = async () => {
      try {
        const notificationsRef = collection(db, "notifications");
        const allNotificationsQuery = query(notificationsRef);
        const allNotificationsSnapshot = await getDocs(allNotificationsQuery);

        allNotificationsSnapshot.docs.forEach((notifDoc) => {
          const notifData = notifDoc.data();
          const seenBy = notifData.seenBy || [];
          if (seenBy.includes(userUID)) {
            const projectId = notifData.projectId || "";
            const taskId = notifData.taskId || "";
            if (projectId && taskId) {
              const key = `${projectId}-${taskId}`;
              lastTaskIds[key] = true;
            }
          }
        });
        console.log(`[Dashboard] Initialized ${Object.keys(lastTaskIds).length} already-seen tasks`);
      } catch (error) {
        console.error("Error initializing seen tasks:", error);
      }
    };

    // Initialize seen tasks first
    initializeSeenTasks();

    projects.forEach((project) => {
      const tasksRef = collection(db, "projects", project.id, "tasks");
      const q = query(tasksRef, orderBy("createdAt", "desc"));
      isFirstSnapshot[project.id] = true; // Mark as first snapshot

      const unsub = onSnapshot(
        q,
        async (snapshot) => {
          const isFirst = isFirstSnapshot[project.id];
          if (isFirst) {
            // On first snapshot after mount, mark all existing tasks as processed
            // This prevents showing notifications for old tasks on hard refresh
            snapshot.docs.forEach((docSnap) => {
              const taskId = docSnap.id;
              const key = `${project.id}-${taskId}`;
              lastTaskIds[key] = true;
            });
            isFirstSnapshot[project.id] = false;
            console.log(`[Dashboard] First snapshot for project ${project.id} - marked ${snapshot.docs.length} existing tasks as processed`);
            return; // Don't process first snapshot
          }

          // Process only new tasks (appearing in subsequent snapshots)
          for (const docSnap of snapshot.docs) {
            const taskData = docSnap.data();
            const taskId = docSnap.id;
            const projectId = project.id;
            const key = `${projectId}-${taskId}`;

            // Skip if we've already processed this task
            if (lastTaskIds[key]) {
              continue;
            }

            // Mark as processed immediately to prevent duplicate notifications
            lastTaskIds[key] = true;

            const taskCreatedBy = taskData.createdBy || "";
            if (taskCreatedBy === userEmail) continue;

            // Don't show popup if user is currently viewing this project's ViewTaskPage
            if (currentProjectId === projectId) {
              console.log(`⏸️ User is viewing project ${projectId}, skipping popup notification`);
              continue;
            }

            // Check if user has already seen this notification in Firestore
            try {
              const notificationsRef = collection(db, "notifications");
              const notificationQuery = query(
                notificationsRef,
                where("projectId", "==", projectId),
                where("taskId", "==", taskId)
              );
              const notificationSnapshot = await getDocs(notificationQuery);

              let shouldShowPopup = true;
              notificationSnapshot.docs.forEach((notifDoc) => {
                const notifData = notifDoc.data();
                const seenBy = notifData.seenBy || [];
                if (seenBy.includes(userUID)) {
                  shouldShowPopup = false;
                  console.log(`⏸️ User has already seen notification for task ${taskId}, skipping popup`);
                }
              });

              if (!shouldShowPopup) continue;
            } catch (error) {
              console.error("Error checking notification seen status:", error);
              // Continue to show popup if check fails
            }

            const addedBy = taskData.createdBy || taskData.createdByName || "Unknown";
            const taskName = taskData.text || taskData.taskName || "New Task";

            showNotification({
              title: "New Task Added",
              body: `${addedBy} added new task: ${taskName}`,
              projectId: projectId,
              projectName: project.name,
              taskId: taskId,
              taskName: taskName,
              addedBy: addedBy,
              addedByName: taskData.createdByName || addedBy,
              link: `/dashboard?projectId=${projectId}&taskId=${taskId}`,
              timestamp: taskData.createdAt?.toMillis?.() || Date.now(),
            });

            // Don't increment badge here - badge count will be updated by notification count listener
            // Badge count is calculated from unread notifications (seenBy array)
            // This ensures badge shows actual unread count, not just increment
            console.log("[Dashboard] New task detected - badge count will update from unread notifications");
          }
        },
        (error) => {
          console.error(`Error listening to tasks for project ${project.id}:`, error);
        }
      );

      unsubscribers.push(unsub);
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userUID, userEmail, projects, showNotification, currentProjectId]);

  // LOAD ALL USERS (for assign & list)
  useEffect(() => {
    fetchAllUsers();
  }, []);

  // Ensure filterDate defaults to today when navigating to attendance page
  useEffect(() => {
    if (activePage === "attendance") {
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!filterDate) {
        setFilterDate(today);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);

  // Fetch all users when in the users page or attendance page (admin only)
  useEffect(() => {
    if (activePage === "users" || (activePage === "attendance" && userRole === "admin")) fetchAllUsers();
    if (activePage === "attendance") {
      fetchAttendanceRecords();
      fetchHolidays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, userRole, filterDate, filterEmail, attendanceViewMode, attendanceCalendarMonth]);

  // LOAD NOTIFICATIONS PER PROJECT (realtime) - For badge counts
  // Query: projectId == project.id && seenBy array does NOT include current user UID
  // Using where("projectId", "==", project.id) and client-side filter for seenBy
  useEffect(() => {
    if (!userUID || projects.length === 0) {
      console.log(`⚠️ Notification listener not set up: userUID=${userUID}, projects.length=${projects.length}`);
      setProjectNotificationCounts({});
      return;
    }

    console.log(`🔔 Setting up notification listeners for ${projects.length} projects, userUID: ${userUID}`);
    const notificationsRef = collection(db, "notifications");
    const unsubscribers = [];

    // Create a listener for each project
    projects.forEach((project) => {
      console.log(`🔔 Setting up listener for project: ${project.name} (${project.id})`);

      // Query: where("projectId", "==", project.id)
      // Filter client-side: where seenBy does NOT include current user UID
      const q = query(
        notificationsRef,
        where("projectId", "==", project.id)
      );

      const unsub = onSnapshot(
        q,
        async (snap) => {
          console.log(`📊 Snapshot received for project ${project.name} (${project.id}): ${snap.docs.length} total notifications`);
          console.log(`📊 Current user UID: ${userUID}`);

          // DEBUG: Show detailed notification data
          console.log(`\n📋 NOTIFICATION DETAILS for ${project.name} (${project.id}):`);
          if (snap.docs.length === 0) {
            console.log(`  ⚠️ NO NOTIFICATIONS FOUND for this project!`);
            console.log(`  ⚠️ This could mean:`);
            console.log(`     - No chats/tasks have been sent yet`);
            console.log(`     - Notifications are being created with wrong projectId`);
            console.log(`     - Query is not matching (projectId mismatch)`);
          } else {
            snap.docs.forEach((docSnap, idx) => {
              const data = docSnap.data();
              const seenBy = data.seenBy || [];
              const isUnseen = !seenBy.includes(userUID);
              const isMention = data.mentionedUser === userUID;

              console.log(`  [${idx + 1}/${snap.docs.length}] ${docSnap.id}`);
              console.log(`      Task: "${(data.taskName || '').substring(0, 40)}..."`);
              console.log(`      projectId: ${data.projectId} (matches ${project.id}? ${data.projectId === project.id})`);
              console.log(`      seenBy:`, seenBy);
              console.log(`      My UID (${userUID}) in seenBy? ${seenBy.includes(userUID)}`);
              console.log(`      mentionedUser: ${data.mentionedUser || 'none'} (isMention? ${isMention})`);
              console.log(`      Will count? ${isUnseen && !isMention} (isUnseen: ${isUnseen}, !isMention: ${!isMention})`);
            });
          }
          console.log(`📋 END\n`);

          // Filter notifications where seenBy does NOT include current user UID
          // Exclude mention notifications (they are tracked separately in projectMentionCounts)
          // Recalculate badges only from unseen notifications
          const unseenNotifications = snap.docs.filter((docSnap) => {
            const data = docSnap.data();
            const seenBy = data.seenBy || [];
            const isMention = data.mentionedUser === userUID; // Exclude mention notifications
            const isUnseen = !seenBy.includes(userUID);
            const shouldCount = isUnseen && !isMention;

            if (shouldCount) {
              console.log(`  ✅ Counting notification ${docSnap.id}: seenBy=${JSON.stringify(seenBy)}, mentionedUser=${data.mentionedUser || 'none'}`);
            } else {
              console.log(`  ❌ Excluding notification ${docSnap.id}: seenBy=${JSON.stringify(seenBy)}, isMention=${isMention}, isUnseen=${isUnseen}`);
            }

            return shouldCount; // Count only unseen non-mention notifications
          });

          const count = unseenNotifications.length;
          console.log(`📊 Project ${project.name} (${project.id}): ${count} unseen notifications out of ${snap.docs.length} total`);

          // Update project notification count
          // Badge will be updated by separate useEffect that watches projectNotificationCounts
          setProjectNotificationCounts((prev) => {
            const newCounts = {
              ...prev,
              [project.id]: count,
            };
            console.log(`📊 Updated notification counts for all projects:`, newCounts);
            return newCounts;
          });
        },
        (err) => {
          console.error(`❌ Notification snapshot error for project ${project.id}:`, err);
        }
      );

      unsubscribers.push(unsub);
    });

    return () => {
      console.log(`🔔 Cleaning up notification listeners for ${unsubscribers.length} projects`);
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userUID, projects]);

  // LOAD MENTION NOTIFICATIONS PER PROJECT (realtime) - For bell icon badge counts
  // Similar to projectNotificationCounts but specifically for @mentions
  useEffect(() => {
    if (!userUID || projects.length === 0) {
      console.log(`⚠️ Mention listener not set up: userUID=${userUID}, projects.length=${projects.length}`);
      setProjectMentionCounts({});
      return;
    }

    console.log(`🔔 Setting up mention listeners for ${projects.length} projects, userUID: ${userUID}`);
    const notificationsRef = collection(db, "notifications");
    const unsubscribers = [];

    // Create a listener for each project
    projects.forEach((project) => {
      console.log(`🔔 Setting up mention listener for project: ${project.name} (${project.id})`);

      const q = query(
        notificationsRef,
        where("projectId", "==", project.id),
        where("mentionedUser", "==", userUID) // Only get notifications where this user is mentioned
      );

      const unsub = onSnapshot(
        q,
        async (snap) => {
          console.log(`🔔 Mention snapshot received for project ${project.name}: ${snap.docs.length} total mention notifications`);

          // Filter notifications where seenBy does NOT include current user UID
          const unseenMentions = snap.docs.filter((docSnap) => {
            const data = docSnap.data();
            const seenBy = data.seenBy || [];
            const isUnseen = !seenBy.includes(userUID);

            if (isUnseen) {
              console.log(`  ✅ Counting mention notification ${docSnap.id}: seenBy=${JSON.stringify(seenBy)}`);
            } else {
              console.log(`  ❌ Excluding seen mention notification ${docSnap.id}: seenBy=${JSON.stringify(seenBy)}`);
            }

            return isUnseen; // Count only unseen mention notifications
          });

          const count = unseenMentions.length;
          console.log(`🔔 Project ${project.name} (${project.id}): ${count} unseen mention notifications out of ${snap.docs.length} total`);

          // Update project mention count
          setProjectMentionCounts((prev) => {
            const newCounts = {
              ...prev,
              [project.id]: count,
            };
            console.log(`🔔 Updated mention counts for all projects:`, newCounts);
            return newCounts;
          });
        },
        (err) => {
          console.error(`❌ Mention notification snapshot error for project ${project.id}:`, err);
        }
      );

      unsubscribers.push(unsub);
    });

    return () => {
      console.log(`🔔 Cleaning up mention listeners for ${unsubscribers.length} projects`);
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userUID, projects]);

  // LEAVE NOTIFICATIONS LISTENER
  useEffect(() => {
    if (!userUID || !userEmail || !userRole) return;

    const notificationsRef = collection(db, "notifications");
    let q;

    if (userRole === "admin") {
      // Admin listens for all leave requests addressed to "admin"
      q = query(
        notificationsRef,
        where("type", "==", "leave_request"),
        where("recipient", "==", "admin")
      );
    } else {
      // Employees listen for leave responses addressed to them
      q = query(
        notificationsRef,
        where("type", "==", "leave_response"),
        where("recipient", "==", userEmail)
      );
    }

    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type === "added") {
          const data = change.doc.data();
          const seenBy = data.seenBy || [];

          if (!seenBy.includes(userUID)) {
            // Check if within last hour to avoid showing very old notifications on mount
            const notifTime = data.createdAt?.toMillis?.() || Date.now();
            if (Date.now() - notifTime < 60 * 60 * 1000) {
              showNotification({
                title: data.title || "Leave Update",
                body: data.body || "",
                projectId: "leave", // dummy to satisfy uniqueness context
                taskId: change.doc.id, // use doc id
                addedBy: data.createdBy || "System",
                addedByName: data.createdBy || "System",
                link: userRole === "admin" ? "/admin/leaves" : "/dashboard",
                timestamp: notifTime,
              });
            }
          }
        }
      });
    }, (err) => {
      console.error("Error listening to leave notifications:", err);
    });

    return () => unsub();
  }, [userUID, userEmail, userRole, showNotification]);

  // Update app icon badge based on total unread notifications across all projects
  useEffect(() => {
    if (!userUID || Object.keys(projectNotificationCounts).length === 0) {
      // If no projects or no counts, clear badge
      clearAppBadge().catch((error) => {
        console.error("[Dashboard] Error clearing badge:", error);
      });
      return;
    }

    // Calculate total unread count across all projects
    const totalUnreadCount = Object.values(projectNotificationCounts).reduce(
      (sum, count) => sum + (count || 0),
      0
    );

    console.log(`📱 Total unread notifications across all projects: ${totalUnreadCount}`);
    console.log(`📊 Project notification counts:`, projectNotificationCounts);

    // Set badge to total unread count (not increment)
    if (totalUnreadCount > 0) {
      setAppBadge(totalUnreadCount)
        .then(() => {
          console.log(`✅ Badge set to ${totalUnreadCount} (total unread notifications)`);
          console.log(`📱 Check app icon - badge should show ${totalUnreadCount}`);
        })
        .catch((error) => {
          console.error("[Dashboard] ❌ Error setting badge from unread count:", error);
          console.error("[Dashboard] Error details:", error.message);
        });
    } else {
      clearAppBadge()
        .then(() => {
          console.log(`✅ Badge cleared (no unread notifications)`);
        })
        .catch((error) => {
          console.error("[Dashboard] ❌ Error clearing badge:", error);
          console.error("[Dashboard] Error details:", error.message);
        });
    }
  }, [projectNotificationCounts, userUID]);

  // LOAD ALL TASKS FOR ADMIN (realtime) - Show all tasks from all projects
  useEffect(() => {
    if (userRole !== "admin" || projects.length === 0) {
      setAdminTasks([]);
      return;
    }

    const unsubscribers = [];

    // Listen to tasks from all projects
    projects.forEach((project) => {
      const tasksRef = collection(db, "projects", project.id, "tasks");
      const q = query(tasksRef, orderBy("createdAt", "desc"));

      const unsub = onSnapshot(
        q,
        (snapshot) => {
          // Update tasks for this specific project
          const projectTasks = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            projectId: project.id,
            projectName: project.name,
            ...docSnap.data(),
          }));

          // Merge with tasks from other projects
          setAdminTasks((prevTasks) => {
            // Remove old tasks from this project
            const otherProjectTasks = prevTasks.filter(
              (t) => t.projectId !== project.id
            );
            // Add new tasks from this project
            const merged = [...otherProjectTasks, ...projectTasks];
            // Sort by createdAt (newest first)
            return merged.sort((a, b) => {
              const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
              const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
              return bTime - aTime;
            });
          });
        },
        (error) => {
          console.error(`Error listening to tasks for project ${project.id}:`, error);
        }
      );

      unsubscribers.push(unsub);
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userRole, projects]);

  // ATTENDANCE HELPER FUNCTIONS
  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return timeString;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const parseTime = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const calculateTotalBreakTime = (breaks) => {
    if (!breaks || breaks.length === 0) return "0 min";

    const totalMinutes = breaks.reduce((total, b) => {
      if (b.start && b.end) {
        const start = parseTime(b.start);
        const end = parseTime(b.end);
        const diff = (end - start) / (1000 * 60);
        return total + Math.max(0, diff);
      }
      return total;
    }, 0);

    if (totalMinutes < 60) {
      return `${Math.round(totalMinutes)} min`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  };

  const formatWorkHours = (minutes) => {
    if (!minutes && minutes !== 0) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // Calculate work hours statistics (by month, week, last day)
  const calculateWorkHoursStats = (records, targetUserEmail = null) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Get start of current week (Monday)
    const startOfWeek = new Date(today);
    const dayOfWeek = startOfWeek.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startOfWeek.setDate(startOfWeek.getDate() - daysToMonday);

    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
    const startOfWeekStr = `${startOfWeek.getFullYear()}-${String(startOfWeek.getMonth() + 1).padStart(2, '0')}-${String(startOfWeek.getDate()).padStart(2, '0')}`;
    const startOfMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

    let thisMonthMinutes = 0;
    let thisWeekMinutes = 0;
    let yesterdayMinutes = 0;

    records.forEach(record => {
      // Filter by user email if specified
      if (targetUserEmail && record.userEmail?.toLowerCase() !== targetUserEmail.toLowerCase()) {
        return;
      }

      if (!record.date) return;
      const workMinutes = record.totalWorkMinutes || 0;

      // Check if record is from yesterday
      if (record.date === yesterdayStr) {
        yesterdayMinutes += workMinutes;
      }

      // Check if record is from this week
      if (record.date >= startOfWeekStr && record.date <= todayStr) {
        thisWeekMinutes += workMinutes;
      }

      // Check if record is from this month
      if (record.date >= startOfMonthStr && record.date <= todayStr) {
        thisMonthMinutes += workMinutes;
      }
    });

    return {
      thisMonth: thisMonthMinutes,
      thisWeek: thisWeekMinutes,
      yesterday: yesterdayMinutes,
      thisMonthFormatted: formatWorkHours(thisMonthMinutes),
      thisWeekFormatted: formatWorkHours(thisWeekMinutes),
      yesterdayFormatted: formatWorkHours(yesterdayMinutes)
    };
  };

  const getAttendanceStatus = (record) => {
    if (record.logoutTime) {
      return <span className="badge bg-success">Completed</span>;
    }
    return <span className="badge bg-warning">Open</span>;
  };

  // Check if regularization can be requested for a record
  const canRequestRegularization = (record) => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    // Can request if:
    // 1. Record is from a previous day (not today)
    // 2. Either work hours < 8h 30m OR missing logout
    // 3. No regularization already requested/approved
    if (record.date === today) return false;
    if (record.regularization?.status === "approved") return false;
    if (record.regularization?.status === "pending") return false;

    const hasIncompleteHours = record.totalWorkMinutes < 510;
    const hasMissingLogout = record.missingLogout === true;

    return hasIncompleteHours || hasMissingLogout;
  };

  // Handle regularization approval (Admin only)
  const handleApproveRegularization = async (recordId) => {
    try {
      await updateDoc(doc(db, "attendance", recordId), {
        regularization: {
          status: "approved",
          approvedAt: new Date().toISOString(),
          approvedBy: userEmail
        },
        totalWorkMinutes: 510 // Set to 8h 30m
      });

      setAttendanceRecords(prev => prev.map(r =>
        r.id === recordId
          ? { ...r, regularization: { ...r.regularization, status: "approved" }, totalWorkMinutes: 510 }
          : r
      ));

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Regularization approved",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error approving regularization:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to approve",
        timer: 2000,
      });
    }
  };

  // Handle regularization rejection (Admin only)
  const handleRejectRegularization = async (recordId) => {
    try {
      await updateDoc(doc(db, "attendance", recordId), {
        "regularization.status": "rejected",
        "regularization.rejectedAt": new Date().toISOString(),
        "regularization.rejectedBy": userEmail
      });

      setAttendanceRecords(prev => prev.map(r =>
        r.id === recordId
          ? { ...r, regularization: { ...r.regularization, status: "rejected" } }
          : r
      ));

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Regularization rejected",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error rejecting regularization:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to reject",
        timer: 2000,
      });
    }
  };

  // Handle regularization request submission
  const handleRegularizationRequest = async (recordId) => {
    const reason = regularizationInputs[recordId]?.reason?.trim();
    if (!reason) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please enter a reason",
        timer: 2000,
      });
      return;
    }

    setSubmittingRegularization(recordId);

    try {
      await updateDoc(doc(db, "attendance", recordId), {
        regularization: {
          status: "pending",
          reason: reason,
          requestedAt: new Date().toISOString(),
        }
      });

      // Update local state
      setAttendanceRecords(prev => prev.map(r =>
        r.id === recordId
          ? { ...r, regularization: { status: "pending", reason: reason } }
          : r
      ));

      // Clear input
      setRegularizationInputs(prev => ({ ...prev, [recordId]: { show: false, reason: "" } }));

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Regularization request sent",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error submitting regularization:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to submit request",
        timer: 2000,
      });
    } finally {
      setSubmittingRegularization(null);
    }
  };

  const toggleRowExpansion = (recordId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId);
    } else {
      newExpanded.add(recordId);
    }
    setExpandedRows(newExpanded);
  };

  // LEAVES LOGIC
  useEffect(() => {
    if (!userEmail || (activePage !== "leaves" && activePage !== "attendance")) return;
    
    // Real-time listener for leaves
    const q = query(
      collection(db, "leaves"),
      where("userEmail", "==", userEmail)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let leavesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort on client side to avoid requiring a composite index in Firestore
      leavesData.sort((a, b) => {
        const timeA = a.appliedAt?.toMillis?.() || 0;
        const timeB = b.appliedAt?.toMillis?.() || 0;
        return timeB - timeA; // Descending
      });
      setMyLeaves(leavesData);
    });
    
    return () => unsubscribe();
  }, [userEmail, activePage]);

  // ADMIN SELECTED USER LEAVES LOGIC
  useEffect(() => {
    if (userRole !== "admin" || activePage !== "attendance" || !filterEmail) {
      setAdminSelectedUserLeaves([]);
      return;
    }
    
    const q = query(
      collection(db, "leaves"),
      where("userEmail", "==", filterEmail)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let leavesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAdminSelectedUserLeaves(leavesData);
    });
    
    return () => unsubscribe();
  }, [userRole, activePage, filterEmail]);

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!leaveFromDate || !leaveToDate || !leaveReason.trim()) {
      Swal.fire("Error", "Please fill all fields.", "error");
      return;
    }
    
    const from = new Date(leaveFromDate);
    const to = new Date(leaveToDate);
    
    // Reset hours to 0 to compare dates accurately
    from.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);
    
    if (to < from) {
      Swal.fire("Error", "To Date cannot be before From Date.", "error");
      return;
    }
    
    // Overlapping dates validation
    const hasOverlap = myLeaves.some(leave => {
      if (leave.status === "rejected") return false;
      const existingFrom = new Date(leave.fromDate);
      const existingTo = new Date(leave.toDate);
      existingFrom.setHours(0, 0, 0, 0);
      existingTo.setHours(0, 0, 0, 0);
      
      // Check for overlap: (StartA <= EndB) and (EndA >= StartB)
      return (from <= existingTo) && (to >= existingFrom);
    });
    
    if (hasOverlap) {
      Swal.fire("Error", "You already have a leave request for one or more of these dates.", "error");
      return;
    }

    setSubmittingLeave(true);
    try {
      const docRef = await addDoc(collection(db, "leaves"), {
        userId: userUID,
        userEmail: userEmail,
        userName: userName || userEmail.split('@')[0],
        leaveType: leaveType,
        fromDate: leaveFromDate,
        toDate: leaveToDate,
        reason: leaveReason.trim(),
        status: "pending",
        appliedAt: serverTimestamp()
      });
      
      // Create a notification for the admin
      try {
        await addDoc(collection(db, "notifications"), {
          type: "leave_request",
          leaveId: docRef.id,
          recipient: "admin",
          title: "New Leave Request",
          body: `${userName || userEmail.split('@')[0]} applied for ${leaveType} leave`,
          createdBy: userEmail,
          createdByUID: userUID,
          createdAt: serverTimestamp(),
          seenBy: []
        });
      } catch (notifErr) {
        console.error("Error creating leave notification:", notifErr);
      }

      Swal.fire("Success", "Leave request submitted successfully.", "success");
      setLeaveType("Casual");
      setLeaveFromDate("");
      setLeaveToDate("");
      setLeaveReason("");
    } catch (error) {
      console.error("Error submitting leave:", error);
      Swal.fire("Error", "Failed to submit leave. Please try again.", "error");
    } finally {
      setSubmittingLeave(false);
    }
  };

  // FETCH ATTENDANCE RECORDS
  const fetchAttendanceRecords = async () => {
    setAttendanceLoading(true);
    try {
      let q;
      if (userRole === "admin") {
        if (attendanceViewMode === "calendar") {
          const startOfMonth = new Date(attendanceCalendarMonth.getFullYear(), attendanceCalendarMonth.getMonth(), 1);
          const endOfMonth = new Date(attendanceCalendarMonth.getFullYear(), attendanceCalendarMonth.getMonth() + 1, 0);
          
          const startDateStr = `${startOfMonth.getFullYear()}-${String(startOfMonth.getMonth() + 1).padStart(2, '0')}-01`;
          const endDateStr = `${endOfMonth.getFullYear()}-${String(endOfMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfMonth.getDate()).padStart(2, '0')}`;
          
          q = query(
            collection(db, "attendance"),
            where("date", ">=", startDateStr),
            where("date", "<=", endDateStr)
          );
        } else if (filterEmail) {
          q = query(
            collection(db, "attendance"),
            where("userEmail", "==", filterEmail)
          );
        } else if (filterDate) {
          q = query(
            collection(db, "attendance"),
            where("date", "==", filterDate)
          );
        } else {
          q = query(collection(db, "attendance")); // Removed orderBy to fix the missing old records
        }
      } else {
        // Non-admins
        q = query(
          collection(db, "attendance"),
          where("userEmail", "==", userEmail)
        );
      }

      const querySnapshot = await getDocs(q);
      const records = [];

      querySnapshot.forEach((doc) => {
        records.push({ 
          id: doc.id, 
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt || 0)
        });
      });
      
      records.sort((a, b) => {
        const aTime = a.createdAt?.getTime() || 0;
        const bTime = b.createdAt?.getTime() || 0;
        return bTime - aTime;
      });

      // Filter by email and date if provided
      let filteredRecords = records;

      if (userRole !== "admin") {
        filteredRecords = filteredRecords.filter((record) =>
          record.userEmail?.toLowerCase() === userEmail?.toLowerCase()
        );
      } else {
        if (filterEmail) {
          filteredRecords = filteredRecords.filter((record) =>
            record.userEmail?.toLowerCase().includes(filterEmail.toLowerCase())
          );
        }
      }

      setAttendanceRecords(filteredRecords);
    } catch (error) {
      console.error("Error fetching attendance records:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to fetch attendance records",
        text: error.message,
        timer: 3000,
      });
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const q = query(collection(db, "holidays"));
      const snapshot = await getDocs(q);
      const fetchedHolidays = [];
      snapshot.forEach(doc => {
        fetchedHolidays.push({ id: doc.id, ...doc.data() });
      });
      setHolidays(fetchedHolidays);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  const handleCompleteTodayHours = async () => {
    if (!filterEmail) return;

    const todayObj = new Date();
    const todayStr = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}-${String(todayObj.getDate()).padStart(2, '0')}`;
    const todayRecord = attendanceRecords.find(r => r.date === todayStr);

    if (!todayRecord) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "No attendance record found for today.",
        timer: 3000,
      });
      return;
    }

    if (todayRecord.totalWorkMinutes >= 510) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Hours are already 8h 30m or more.",
        timer: 3000,
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: 'Complete Today\'s Hours?',
      text: `Are you sure you want to complete today's hours to 8h 30m for ${filterEmail}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, complete it!'
    });

    if (confirmResult.isConfirmed) {
      try {
        const updateData = {
          totalWorkMinutes: 510,
          manuallyAdjustedByAdmin: true
        };

        if (!todayRecord.logoutTime) {
          updateData.logoutTime = "18:30";
        }

        await updateDoc(doc(db, "attendance", todayRecord.id), updateData);
        
        // Update local state
        setAttendanceRecords(prev => prev.map(r => 
          r.id === todayRecord.id 
            ? { ...r, ...updateData }
            : r
        ));

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Hours updated to 8h 30m successfully",
          timer: 3000,
        });
      } catch (error) {
        console.error("Error updating hours: ", error);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to update hours",
          timer: 3000,
        });
      }
    }
  };

  const handleDateClick = async (date, status, holidayName) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    if (filterEmail) {
      // Employee is selected. Handle completing hours for clicked date.
      const record = attendanceRecords.find(r => r.date === dateStr);

      if (!record) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "No attendance record found for this date.",
          timer: 3000,
        });
        return;
      }

      if (record.totalWorkMinutes >= 510) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "info",
          title: "Hours are already 8h 30m or more.",
          timer: 3000,
        });
        return;
      }

      const confirmResult = await Swal.fire({
        title: 'Complete Hours?',
        text: `Are you sure you want to complete hours to 8h 30m for ${dateStr}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, complete it!'
      });

      if (confirmResult.isConfirmed) {
        try {
          const updateData = {
            totalWorkMinutes: 510,
            manuallyAdjustedByAdmin: true
          };
          
          if (!record.logoutTime) {
            updateData.logoutTime = "18:30"; // Set a dummy logout time so it doesn't show as Missing Logout
          }

          await updateDoc(doc(db, "attendance", record.id), updateData);
          
          setAttendanceRecords(prev => prev.map(r => 
            r.id === record.id 
              ? { ...r, ...updateData }
              : r
          ));

          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: "Hours updated successfully",
            timer: 3000,
          });
        } catch (error) {
          console.error("Error updating hours: ", error);
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "error",
            title: "Failed to update hours",
            timer: 3000,
          });
        }
      }
      return;
    }
    
    // Only allow marking holidays on Global Calendar (when no employee is selected)
    
    if (status === 'HOLIDAY') {
      const holidayDoc = holidays.find(h => h.date === dateStr);
      if (!holidayDoc) return;

      const result = await Swal.fire({
        title: 'Remove Holiday?',
        text: `Do you want to remove "${holidayName}" as a holiday on ${dateStr}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, remove it!'
      });

      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, "holidays", holidayDoc.id));
          Swal.fire('Removed!', 'The holiday has been removed.', 'success');
          fetchHolidays();
        } catch (error) {
          console.error("Error removing holiday:", error);
          Swal.fire('Error', 'Failed to remove holiday', 'error');
        }
      }
    } else {
      const { value: formValues } = await Swal.fire({
        title: 'Add Holiday',
        html:
          `<p>Date: ${dateStr}</p>` +
          '<input id="swal-holiday-name" class="swal2-input" placeholder="Holiday Name (e.g., Diwali)">',
        focusConfirm: false,
        showCancelButton: true,
        preConfirm: () => {
          const name = document.getElementById('swal-holiday-name').value;
          if (!name) {
            Swal.showValidationMessage('Holiday name is required');
          }
          return { name };
        }
      });

      if (formValues) {
        try {
          await addDoc(collection(db, "holidays"), {
            date: dateStr,
            name: formValues.name,
            createdBy: auth.currentUser?.email || "admin",
            createdAt: new Date().toISOString()
          });
          Swal.fire('Added!', 'Holiday has been saved.', 'success');
          fetchHolidays();
        } catch (error) {
          console.error("Error adding holiday:", error);
          Swal.fire('Error', 'Failed to save holiday', 'error');
        }
      }
    }
  };

  // FUNCTIONS

  const addProject = async () => {
    if (!projectName.trim()) {
      return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Enter project name" });
    }
    if (userRole !== "admin") {
      return Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Only admin can create projects" });
    }

    try {
      await addDoc(collection(db, "projects"), {
        name: projectName,
        createdBy: userUID,
        users: [userUID],
        createdAt: serverTimestamp(),
      });
      setProjectName("");
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Project created" });
    } catch (err) {
      console.error(err);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Failed to create project" });
    }
  };

  const addNewUser = async () => {
    if (!newUsername.trim() || !newEmail.trim()) {
      return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Please fill all fields" });
    }
    if (userRole !== "admin") {
      return Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Only admin can add users" });
    }

    try {
      const defaultPassword = "12345678";
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newEmail, defaultPassword);
      const uid = userCredential.user.uid;

      await setDoc(doc(db, "users", uid), {
        username: newUsername,
        email: newEmail,
        role: newRole,
        uid,
        createdAt: serverTimestamp(),
      });

      await signOut(secondaryAuth);

      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "User created!" });

      setNewUsername("");
      setNewEmail("");
      setNewRole("user");
      setShowUserModal(false);
      fetchAllUsers();
    } catch (err) {
      console.error(err);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: err.message || "Failed to create user" });
    }
  };

  const updateProject = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateDoc(doc(db, "projects", id), { name: editName });
      setEditId(null);
      setEditName("");
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Updated" });
    } catch (err) {
      console.error(err);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Update failed" });
    }
  };

  const deleteProject = async (id) => {
    const confirm = await Swal.fire({ title: "Delete Project?", icon: "warning", showCancelButton: true, confirmButtonText: "Delete" });
    if (!confirm.isConfirmed) return;
    try {
      await deleteDoc(doc(db, "projects", id));
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Deleted" });
    } catch (err) {
      console.error(err);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Delete failed" });
    }
  };

  const openAssignModal = (project) => {
    if (userRole !== "admin") {
      return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Only admin can assign users" });
    }
    setSelectedProject(project);
    setAssignedUsers(project.users || []);
    setShowAssignModal(true);
  };

  const toggleUser = (uid) => {
    setAssignedUsers((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));
  };

  const saveAssignedUsers = async () => {
    if (!selectedProject) return;

    try {
      console.log("💾 Saving assigned users to project:", selectedProject.id);
      console.log("   Project name:", selectedProject.name);
      console.log("   Assigned users:", assignedUsers);
      console.log("   Assigned user count:", assignedUsers.length);

      // Verify all user UIDs exist
      const validUsers = [];
      for (const uid of assignedUsers) {
        const userExists = allUsers.find(u => u.id === uid);
        if (userExists) {
          validUsers.push(uid);
          console.log(`   ✅ User ${uid} (${userExists.email}) is valid`);
        } else {
          console.warn(`   ⚠️ User ${uid} not found in allUsers list`);
        }
      }

      if (validUsers.length !== assignedUsers.length) {
        console.warn("⚠️ Some users were not found, using only valid users");
      }

      const finalUsers = validUsers.length > 0 ? validUsers : assignedUsers;

      if (finalUsers.length === 0) {
        console.error("❌ No valid users to assign!");
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "No valid users to assign",
          text: "Please select at least one valid user"
        });
        return;
      }

      console.log("\n📤 Updating project in Firestore...");
      console.log("   Final users array:", finalUsers);
      console.log("   Final users count:", finalUsers.length);

      // Update project with assigned users
      const projectRef = doc(db, "projects", selectedProject.id);
      await updateDoc(projectRef, {
        users: finalUsers
      });

      console.log("✅ Firestore update completed");

      // Verify the update was successful
      const updatedDoc = await getDoc(projectRef);
      if (updatedDoc.exists()) {
        const updatedData = updatedDoc.data();
        console.log("\n✅ Project updated successfully!");
        console.log("   Updated users array:", updatedData.users);
        console.log("   Total users in project:", updatedData.users?.length || 0);

        // Log each assigned user's UID for debugging
        if (updatedData.users && updatedData.users.length > 0) {
          console.log("\n   Assigned Users List:");
          updatedData.users.forEach((uid, index) => {
            const user = allUsers.find(u => u.id === uid);
            console.log(`   ${index + 1}. ${uid} (${user?.email || user?.username || 'Unknown'})`);
          });
        } else {
          console.warn("   ⚠️ WARNING: Users array is empty!");
        }

        // Verify current user can see this project
        const currentUserInProject = updatedData.users?.includes(userUID);
        console.log(`\n   Current logged-in user (${userUID}) in project:`, currentUserInProject ? "✅ YES" : "❌ NO");
        if (!currentUserInProject && userRole === "admin") {
          console.log("   ℹ️ Note: Admin can see all projects, but this user won't see it if not in array");
        }
      } else {
        console.error("❌ ERROR: Project document not found after update!");
      }

      setShowAssignModal(false);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `Users Assigned (${finalUsers.length} users)`,
        text: "Assigned users will see the project in their dashboard"
      });
    } catch (err) {
      console.error("❌ Error saving assigned users:", err);
      console.error("   Error code:", err.code);
      console.error("   Error message:", err.message);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to assign users",
        text: err.message
      });
    }
  };

  const handleLogout = async () => {
    // Mark logout as intentional before signing out
    // This flag will be checked in auth listener
    window.__intentionalLogout = true;
    await signOut(auth);
    navigate("/");
  };

  const fetchAllUsers = async () => {
    try {
      const usersRef = collection(db, "users");
      const snapshot = await getDocs(usersRef);
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllUsers(list);
    } catch (err) {
      console.error("fetchAllUsers error:", err);
    }
  };

  const deleteUser = async (id) => {
    // Find the user being deleted
    const userToDelete = allUsers.find(u => u.id === id);

    // Count total number of admins
    const adminCount = allUsers.filter(u => u.role === "admin").length;

    // Prevent deletion if this is the last admin
    if (userToDelete?.role === "admin" && adminCount === 1) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Cannot Delete Last Admin",
        text: "At least one admin user must exist in the system",
        timer: 4000,
        showConfirmButton: false
      });
      return;
    }

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Delete user: ${userToDelete?.email || 'this user'}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        await deleteDoc(doc(db, "users", id));
        setAllUsers((prev) => prev.filter((u) => u.id !== id));
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "User deleted successfully!",
          timer: 3000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error deleting user:", error);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to delete user",
          text: error.message,
          timer: 3000,
        });
      }
    }
  };

  const handleView = (id, name) => {
    // Set embedded chat view (same as board view)
    setActivePage("dashboard"); // Ensure we're on dashboard page
    setSelectedProjectId(id);
    setSelectedProjectName(name);
    setProjectViewMode("chat");
    if (isMobile) setSidebarOpen(false);
  };

  const handleBoardView = (id, name) => {
    // Set embedded board view
    setActivePage("dashboard"); // Ensure we're on dashboard page
    setSelectedProjectId(id);
    setSelectedProjectName(name);
    setProjectViewMode("board");
    if (isMobile) setSidebarOpen(false);
  };


  // Debug function - Check project assignment
  // Can be called from browser console: window.checkProjectAssignment()
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.checkProjectAssignment = async () => {
        console.log("🔍 Checking Project Assignment Status...");
        console.log("   Current User UID:", userUID);
        console.log("   Current User Email:", userEmail);
        console.log("   Projects in state:", projects.length);

        if (!userUID) {
          console.error("❌ User not logged in");
          return;
        }

        try {
          // Get all projects from Firestore
          const allProjectsSnapshot = await getDocs(collection(db, "projects"));
          console.log(`\n📊 All Projects in Firestore: ${allProjectsSnapshot.docs.length}`);

          allProjectsSnapshot.docs.forEach((docSnap) => {
            const projectData = docSnap.data();
            const projectUsers = projectData.users || [];
            const isUserAssigned = projectUsers.includes(userUID);

            console.log(`\n   Project: ${projectData.name} (ID: ${docSnap.id})`);
            console.log(`     Total assigned users: ${projectUsers.length}`);
            console.log(`     Users array:`, projectUsers);
            console.log(`     Current user (${userUID}) assigned:`, isUserAssigned ? "✅ YES" : "❌ NO");

            if (isUserAssigned) {
              const isInState = projects.find(p => p.id === docSnap.id);
              console.log(`     In dashboard state:`, isInState ? "✅ YES" : "❌ NO");
            }
          });

          // Check projects in state
          console.log(`\n📋 Projects in Dashboard State: ${projects.length}`);
          projects.forEach((p) => {
            console.log(`   - ${p.name} (ID: ${p.id})`);
            console.log(`     Users: ${(p.users || []).length}`);
          });

        } catch (error) {
          console.error("❌ Error checking projects:", error);
        }
      };
    }
  }, [userUID, userEmail, projects]);

  // RENDER
  // Show loading screen until auth check is complete
  if (!authChecked) {
    return (
      <div className="container-fluid p-0 d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  const displayedAttendanceRecords = attendanceViewMode !== "calendar" && filterDate 
    ? attendanceRecords.filter(record => record.date === filterDate)
    : attendanceRecords;

  return (
    <div className="container-fluid p-0">
      {/* TOP HEADER - Responsive Design */}
      <div
        style={{
          backgroundColor: "#111827",
          height: isMobile ? "56px" : "64px",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "0 16px" : "0 24px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "12px" : "16px", flex: "1", minWidth: 0 }}>
          {/* Mobile Menu Toggle Button */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                backgroundColor: "transparent",
                border: "none",
                color: "#fff",
                fontSize: isMobile ? "24px" : "20px",
                cursor: "pointer",
                padding: isMobile ? "8px" : "4px 8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                transition: "background-color 0.2s",
                minWidth: isMobile ? "44px" : "auto",
                minHeight: isMobile ? "44px" : "auto"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg width={isMobile ? "24" : "20"} height={isMobile ? "24" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {sidebarOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" />
                ) : (
                  <path d="M3 12h18M3 6h18M3 18h18" />
                )}
              </svg>
            </button>
          )}
          <div style={{
            color: "#fff",
            fontSize: isMobile ? "16px" : "18px",
            fontWeight: "600",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            {userRole === "admin" ? "Admin Dashboard" : "Employee Dashboard"}
          </div>
        </div>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "8px" : "12px",
          flexWrap: "nowrap",
          flexShrink: 0
        }}>
          {/* Mute Notifications Toggle - Admin Only */}
          {userRole === "admin" && (
            <button
              onClick={() => {
                const newMutedState = !notificationsMuted;
                setNotificationsMuted(newMutedState);
                localStorage.setItem("notificationsMuted", String(newMutedState));
                Swal.fire({
                  toast: true,
                  position: "top-end",
                  icon: newMutedState ? "info" : "success",
                  title: newMutedState ? "Notifications muted" : "Notifications enabled",
                  timer: 2000,
                });
              }}
              style={{
                backgroundColor: notificationsMuted ? "#ef4444" : "#6b7280",
                color: "#fff",
                border: "none",
                padding: isMobile ? "8px" : "8px 12px",
                borderRadius: "8px",
                fontSize: isMobile ? "14px" : "12px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                minWidth: isMobile ? "44px" : "auto",
                minHeight: isMobile ? "44px" : "auto",
                justifyContent: "center"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.9";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.1)";
              }}
              title={notificationsMuted ? "Unmute notifications" : "Mute notifications"}
            >
              {notificationsMuted ? (
                <svg width={isMobile ? "18" : "14"} height={isMobile ? "18" : "14"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5L6 9H2V15H6L11 19V5Z" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                <svg width={isMobile ? "18" : "14"} height={isMobile ? "18" : "14"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5L6 9H2V15H6L11 19V5Z" />
                  <path d="M19.07 4.93C20.9447 6.79858 21.9979 9.30219 21.9979 12C21.9979 14.6978 20.9447 17.2014 19.07 19.07" />
                  <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 12C17.0039 13.3308 16.4774 14.6024 15.54 15.54" />
                </svg>
              )}
              {!isMobile && !isExtraSmall && (notificationsMuted ? "Unmute" : "Mute")}
            </button>
          )}

          {/* Join Meeting Button */}
          <button
            onClick={() => window.open("https://meet.google.com/tgf-moxx-kgt", "_blank")}
            style={{
              backgroundColor: "#f3f4f6",
              color: "#374151",
              border: "1px solid #e5e7eb",
              padding: isMobile ? "8px" : "8px 16px",
              borderRadius: "8px",
              fontSize: isMobile ? "14px" : "13px",
              fontWeight: "500",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s ease",
              minWidth: isMobile ? "44px" : "auto",
              minHeight: isMobile ? "44px" : "auto",
              justifyContent: "center"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e5e7eb";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f3f4f6";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
            title="Join Meeting"
          >
            <svg
              width={isMobile ? "18" : "14"}
              height={isMobile ? "18" : "14"}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 7l-7 5 7 5V7z" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
            {!isMobile && !isExtraSmall && "Join Meeting"}
          </button>

          {/* Official Meeting Button - Admin Only */}
          {userRole === "admin" && (
            <button
              onClick={() => window.open("https://meet.google.com/jhx-hxoa-dqi", "_blank")}
              style={{
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #e5e7eb",
                padding: isMobile ? "8px" : "8px 16px",
                borderRadius: "8px",
                fontSize: isMobile ? "14px" : "13px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
                minWidth: isMobile ? "44px" : "auto",
                minHeight: isMobile ? "44px" : "auto",
                justifyContent: "center"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e5e7eb";
                e.currentTarget.style.borderColor = "#d1d5db";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
              title="Official Meeting"
            >
              <svg
                width={isMobile ? "18" : "14"}
                height={isMobile ? "18" : "14"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 7l-7 5 7 5V7z" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                <circle cx="8" cy="12" r="2" fill="currentColor" />
              </svg>
              {!isMobile && !isExtraSmall && "Official Meeting"}
            </button>
          )}

          {/* User Email - Hidden on very small screens */}
          {!isMobile && (
            <div style={{ color: "#9ca3af", fontSize: "12px", whiteSpace: "nowrap" }}>
              {userEmail}
            </div>
          )}
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div className="d-flex" style={{ minHeight: "100vh", paddingTop: isMobile ? "56px" : "64px" }}>
        {/* Mobile Overlay Backdrop */}
        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: "fixed",
              top: "56px",
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 150,
              transition: "opacity 0.3s ease"
            }}
          />
        )}

        {/* SIDEBAR */}
        <div
          className={`sidebar-responsive ${isMobile ? '' : 'd-none d-md-flex'} flex-column`}
          style={{
            width: isMobile ? "min(75vw, 300px)" : "240px",
            backgroundColor: "#fff",
            borderRight: "1px solid #e5e7eb",
            position: "fixed",
            top: isMobile ? "56px" : "64px",
            left: isMobile ? (sidebarOpen ? 0 : "min(-75vw, -300px)") : 0,
            height: isMobile ? "calc(100vh - 56px)" : "calc(100vh - 64px)",
            zIndex: 160,
            transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            overflowY: "auto",
            boxShadow: isMobile && sidebarOpen ? "4px 0 12px rgba(0, 0, 0, 0.15)" : "none"
          }}
        >
          {/* Logo/Brand */}
          <div style={{ padding: isMobile ? "20px 16px" : "20px 16px", borderBottom: "1px solid #e5e7eb" }}>
            <img
              src="/jis-bold.jpg"
              alt="JoyInfoSolutions"
              style={{
                height: isMobile ? "36px" : "36px",
                width: "auto",
                display: "block",
                marginBottom: "8px"
              }}
            />
            <div style={{
              fontSize: isMobile ? "12px" : "10px",
              color: "#6b7280",
              fontWeight: "600",
              letterSpacing: "0.025em",
              textTransform: "uppercase"
            }}>
              {userRole === "admin" ? "Admin Panel" : "Employee Portal"}
            </div>
          </div>

          {/* Navigation */}
          <nav style={{ padding: isMobile ? "12px" : "0 12px", flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {/* Mark Attendance - Only for non-admin and not on mobile device */}
            {/* Always check function to detect mobile even in desktop mode */}
            {userUID && userRole !== "admin" && !checkMobileDevice() && (
              <button
                className="btn w-100 text-start mb-3"
                onClick={() => {
                  // Double check before opening modal - prevent mobile access
                  if (checkMobileDevice()) {
                    Swal.fire({
                      toast: true,
                      position: "top-end",
                      icon: "error",
                      title: "Mark Attendance is not available on mobile devices",
                      text: "Please use a desktop or laptop computer to mark attendance.",
                      timer: 4000,
                    });
                    return;
                  }
                  setShowAttendanceModal(true);
                }}
                style={{
                  padding: isMobile ? "12px 14px" : "10px 14px",
                  backgroundColor: "#10b981",
                  color: "#fff",
                  fontSize: isMobile ? "14px" : "13px",
                  fontWeight: "600",
                  border: "none",
                  borderRadius: "6px",
                  minHeight: isMobile ? "44px" : "auto"
                }}
              >
                + Mark Attendance
              </button>
            )}

            {/* HOME Button - First Item */}
            <button
              className="btn w-100 text-start"
              onClick={() => {
                setActivePage("dashboard");
                setSelectedProjectId(null);
                setSelectedProjectName("");
                setProjectViewMode("chat");
                if (isMobile) setSidebarOpen(false);
              }}
              style={{
                padding: isMobile ? "14px 16px" : "10px 14px",
                backgroundColor: activePage === "dashboard" && !selectedProjectId ? "#f3f4f6" : "transparent",
                color: activePage === "dashboard" && !selectedProjectId ? "#111827" : "#6b7280",
                fontSize: isMobile ? "15px" : "14px",
                fontWeight: activePage === "dashboard" && !selectedProjectId ? "600" : "500",
                border: "none",
                borderRadius: "8px",
                marginBottom: "8px",
                minHeight: isMobile ? "48px" : "auto",
                transition: "all 0.15s ease",
                display: "flex",
                alignItems: "center",
                gap: "12px"
              }}
              onMouseEnter={(e) => {
                if (activePage !== "dashboard" || selectedProjectId) {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.color = "#374151";
                }
              }}
              onMouseLeave={(e) => {
                if (activePage !== "dashboard" || selectedProjectId) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#6b7280";
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              HOME
            </button>

            {/* MY ATTENDANCE / ALL ATTENDANCE Button - Below HOME */}
            <button
              className="btn w-100 text-start"
              onClick={() => {
                setActivePage("attendance");
                if (isMobile) setSidebarOpen(false);
              }}
              style={{
                padding: isMobile ? "14px 16px" : "10px 14px",
                backgroundColor: activePage === "attendance" ? "#f3f4f6" : "transparent",
                color: activePage === "attendance" ? "#111827" : "#6b7280",
                fontSize: isMobile ? "15px" : "14px",
                fontWeight: activePage === "attendance" ? "600" : "500",
                border: "none",
                borderRadius: "8px",
                marginBottom: "8px",
                minHeight: isMobile ? "48px" : "auto",
                transition: "all 0.15s ease",
                display: "flex",
                alignItems: "center",
                gap: "12px"
              }}
              onMouseEnter={(e) => {
                if (activePage !== "attendance") {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.color = "#374151";
                }
              }}
              onMouseLeave={(e) => {
                if (activePage !== "attendance") {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#6b7280";
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {userRole === "admin" ? "ALL ATTENDANCE" : "MY ATTENDANCE"}
            </button>

            {/* LEAVES Button */}
            {userRole !== "admin" && (
              <button
              className="btn w-100 text-start"
              onClick={() => {
                setActivePage("leaves");
                if (isMobile) setSidebarOpen(false);
              }}
              style={{
                padding: isMobile ? "14px 16px" : "10px 14px",
                backgroundColor: activePage === "leaves" ? "#f3f4f6" : "transparent",
                color: activePage === "leaves" ? "#111827" : "#6b7280",
                fontSize: isMobile ? "15px" : "14px",
                fontWeight: activePage === "leaves" ? "600" : "500",
                border: "none",
                borderRadius: "8px",
                marginBottom: "8px",
                minHeight: isMobile ? "48px" : "auto",
                transition: "all 0.15s ease",
                display: "flex",
                alignItems: "center",
                gap: "12px"
              }}
              onMouseEnter={(e) => {
                if (activePage !== "leaves") {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.color = "#374151";
                }
              }}
              onMouseLeave={(e) => {
                if (activePage !== "leaves") {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#6b7280";
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
              </svg>
              APPLY LEAVE & MY LEAVES
            </button>
            )}

            {userRole === "admin" && (
              <>
              <button
                className="btn w-100 text-start"
                onClick={() => {
                  setActivePage("all_leaves");
                  if (isMobile) setSidebarOpen(false);
                }}
                style={{
                  padding: isMobile ? "14px 16px" : "10px 14px",
                  backgroundColor: activePage === "all_leaves" ? "#f3f4f6" : "transparent",
                  color: activePage === "all_leaves" ? "#111827" : "#6b7280",
                  fontSize: isMobile ? "15px" : "14px",
                  fontWeight: activePage === "all_leaves" ? "600" : "500",
                  border: "none",
                  borderRadius: "8px",
                  marginBottom: "8px",
                  minHeight: isMobile ? "48px" : "auto",
                  transition: "all 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.color = "#374151";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#6b7280";
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                ALL LEAVES
              </button>
              <button
                className="btn w-100 text-start"
                onClick={() => {
                  setActivePage("users");
                  if (isMobile) setSidebarOpen(false);
                }}
                style={{
                  padding: isMobile ? "14px 16px" : "10px 14px",
                  backgroundColor: activePage === "users" ? "#f3f4f6" : "transparent",
                  color: activePage === "users" ? "#111827" : "#6b7280",
                  fontSize: isMobile ? "15px" : "14px",
                  fontWeight: activePage === "users" ? "600" : "500",
                  border: "none",
                  borderRadius: "8px",
                  marginBottom: "8px",
                  minHeight: isMobile ? "48px" : "auto",
                  transition: "all 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px"
                }}
                onMouseEnter={(e) => {
                  if (activePage !== "users") {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                    e.currentTarget.style.color = "#374151";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== "users") {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#6b7280";
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                USERS
              </button>
              </>
            )}

            {/* Spacer to push user profile to bottom */}
            <div style={{ flex: 1 }}></div>
          </nav>

          {/* User Profile & Logout */}
          <div style={{ padding: isMobile ? "12px 8px" : "16px 12px", borderTop: "1px solid #e5e7eb" }}>
            <div className="d-flex align-items-center gap-3 mb-3" style={{ padding: "0 8px" }}>
              <div
                style={{
                  width: isMobile ? "40px" : "32px",
                  height: isMobile ? "40px" : "32px",
                  backgroundColor: "#dbeafe",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontSize: isMobile ? "16px" : "13px",
                  fontWeight: "600",
                  color: "#2563eb"
                }}
              >
                {firstLetter}
              </div>
              <div>
                <div style={{ fontSize: isMobile ? "14px" : "13px", color: "#111827", fontWeight: "600" }}>
                  {(userName || userEmail?.split("@")[0] || "User").split(/[._]/)[0].charAt(0).toUpperCase() + (userName || userEmail?.split("@")[0] || "User").split(/[._]/)[0].slice(1)}
                </div>
                <div style={{ fontSize: isMobile ? "12px" : "11px", color: "#9ca3af" }}>{userRole}</div>
              </div>
            </div>
            <button
              className="btn w-100 text-start"
              onClick={handleLogout}
              style={{
                padding: isMobile ? "12px 14px" : "8px 14px",
                backgroundColor: "transparent",
                color: "#6b7280",
                fontSize: isMobile ? "14px" : "13px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                minHeight: isMobile ? "44px" : "auto"
              }}
            >
              Sign out
            </button>
            {showInstallButton && (
              <button
                onClick={handleInstallClick}
                className="btn w-100 text-start mt-1"
                style={{
                  padding: isMobile ? "12px 14px" : "8px 14px",
                  backgroundColor: "transparent",
                  color: "#6b7280",
                  fontSize: isMobile ? "14px" : "13px",
                  fontWeight: "500",
                  border: "none",
                  borderRadius: "6px",
                  minHeight: isMobile ? "44px" : "auto"
                }}
              >
                Install App
              </button>
            )}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div
          className="flex-grow-1 main-content-responsive"
          style={{
            backgroundColor: "#f9fafb",
            minHeight: "100vh",
            marginLeft: isMobile ? "0" : "240px",
            width: isMobile ? "100%" : "calc(100% - 240px)",
            transition: "margin-left 0.3s ease"
          }}
        >
          <div style={{
            maxWidth: selectedProjectId && (projectViewMode === "board" || projectViewMode === "chat") ? "100%" : "1200px",
            margin: "0 auto",
            padding: isMobile
              ? (selectedProjectId && (projectViewMode === "board" || projectViewMode === "chat") ? "8px 12px" : "16px 12px")
              : (selectedProjectId && (projectViewMode === "board" || projectViewMode === "chat") ? "12px 24px" : "32px")
          }}>
            {/* Conditional pages */}
            {activePage === "dashboard" && (
              <>
                {/* PROJECT VIEW - Show when a project is selected */}
                {selectedProjectId ? (
                  <div style={{ width: "100%" }}>
                    {/* Project Header Card with Tabs */}
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "0",
                        marginBottom: "12px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      {/* Single Combined Header - All details in one section */}
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          padding: isMobile ? "12px 16px" : "8px 24px",
                          borderBottom: "1px solid #e5e7eb",
                          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)"
                        }}
                      >
                        <div className="d-flex align-items-center gap-3" style={{ flexWrap: "wrap" }}>
                          {/* Left: Tabs */}
                          <div className="d-flex align-items-center gap-2" style={{ flex: "0 0 auto", flexWrap: isMobile ? "wrap" : "nowrap" }}>
                            <button
                              onClick={() => setProjectViewMode("chat")}
                              style={{
                                backgroundColor: projectViewMode === "chat" ? "#f3f4f6" : "transparent",
                                color: projectViewMode === "chat" ? "#5b6770" : "#64748b",
                                border: "none",
                                borderRadius: "6px",
                                padding: isMobile ? "10px 16px" : "6px 14px",
                                fontSize: isMobile ? "15px" : "14px",
                                fontWeight: projectViewMode === "chat" ? "600" : "500",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                                minHeight: isMobile ? "44px" : "auto",
                                minWidth: isMobile ? "80px" : "auto"
                              }}
                              onMouseEnter={(e) => {
                                if (projectViewMode !== "chat") {
                                  e.currentTarget.style.backgroundColor = "#f9fafb";
                                  e.currentTarget.style.color = "#5b6770";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (projectViewMode !== "chat") {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.color = "#64748b";
                                }
                              }}
                            >
                              Chat
                            </button>
                            <button
                              onClick={() => setProjectViewMode("board")}
                              style={{
                                backgroundColor: projectViewMode === "board" ? "#f3f4f6" : "transparent",
                                color: projectViewMode === "board" ? "#5b6770" : "#64748b",
                                border: "none",
                                borderRadius: "6px",
                                padding: isMobile ? "10px 16px" : "6px 14px",
                                fontSize: isMobile ? "15px" : "14px",
                                fontWeight: projectViewMode === "board" ? "600" : "500",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                                minHeight: isMobile ? "44px" : "auto",
                                minWidth: isMobile ? "80px" : "auto"
                              }}
                              onMouseEnter={(e) => {
                                if (projectViewMode !== "board") {
                                  e.currentTarget.style.backgroundColor = "#f9fafb";
                                  e.currentTarget.style.color = "#5b6770";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (projectViewMode !== "board") {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.color = "#64748b";
                                }
                              }}
                            >
                              Board
                            </button>

                            {/* Unassigned Dropdown - Only for Board View */}
                            {projectViewMode === "board" && (
                              <div style={{ marginLeft: isMobile ? "0" : "8px", marginTop: isMobile ? "8px" : "0", position: "relative", display: "flex", alignItems: "center", width: isMobile ? "100%" : "auto" }}>
                                <i
                                  className="bi bi-person-fill"
                                  style={{
                                    position: "absolute",
                                    left: isMobile ? "12px" : "10px",
                                    zIndex: 1,
                                    color: "#6b7280",
                                    fontSize: isMobile ? "16px" : "14px",
                                    pointerEvents: "none"
                                  }}
                                />
                                <select
                                  value={selectedBoardUser}
                                  onChange={(e) => setSelectedBoardUser(e.target.value)}
                                  style={{
                                    padding: isMobile ? "10px 28px 10px 36px" : "6px 28px 6px 30px",
                                    borderRadius: isMobile ? "8px" : "6px",
                                    border: "1px solid #e5e7eb",
                                    backgroundColor: "#ffffff",
                                    fontSize: isMobile ? "15px" : "13px",
                                    fontWeight: "500",
                                    color: "#4b5563",
                                    cursor: "pointer",
                                    outline: "none",
                                    appearance: "none",
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "right 8px center",
                                    backgroundSize: "12px",
                                    minWidth: isMobile ? "100%" : "130px",
                                    width: isMobile ? "100%" : "auto",
                                    height: isMobile ? "44px" : "34px"
                                  }}
                                >
                                  <option value="Unassigned">Unassigned</option>
                                  <option value="All">All Users</option>
                                  {selectedProjectId && projects.find(p => p.id === selectedProjectId)?.users?.map(uid => {
                                    const user = allUsers.find(u => u.id === uid);
                                    return (
                                      <option key={uid} value={uid}>
                                        {user?.username || user?.email || "Unknown"}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>
                            )}

                            {/* Date Range Filter - Only for Board View */}
                            {projectViewMode === "board" && (
                              <div style={{
                                marginLeft: isMobile ? "0" : "8px",
                                marginTop: isMobile ? "8px" : "0",
                                display: "flex",
                                alignItems: "center",
                                gap: isMobile ? "8px" : "6px",
                                flexWrap: isMobile ? "wrap" : "nowrap",
                                width: isMobile ? "100%" : "auto"
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "4px", flex: isMobile ? "1 1 calc(50% - 4px)" : "0 0 auto" }}>
                                  <label style={{ fontSize: isMobile ? "13px" : "12px", color: "#6b7280", fontWeight: "500", whiteSpace: "nowrap" }}>From:</label>
                                  <input
                                    type="date"
                                    value={boardFromDate}
                                    onChange={(e) => setBoardFromDate(e.target.value)}
                                    style={{
                                      padding: isMobile ? "8px" : "4px 8px",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: isMobile ? "8px" : "6px",
                                      fontSize: isMobile ? "14px" : "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      outline: "none",
                                      height: isMobile ? "44px" : "34px",
                                      width: isMobile ? "100%" : "120px",
                                      flex: isMobile ? "1" : "0 0 auto"
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = "#0ea5e9";
                                      e.target.style.boxShadow = "0 0 0 2px rgba(14, 165, 233, 0.1)";
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = "#e5e7eb";
                                      e.target.style.boxShadow = "none";
                                    }}
                                    max={boardToDate || undefined}
                                  />
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "4px", flex: isMobile ? "1 1 calc(50% - 4px)" : "0 0 auto" }}>
                                  <label style={{ fontSize: isMobile ? "13px" : "12px", color: "#6b7280", fontWeight: "500", whiteSpace: "nowrap" }}>To:</label>
                                  <input
                                    type="date"
                                    value={boardToDate}
                                    onChange={(e) => setBoardToDate(e.target.value)}
                                    style={{
                                      padding: isMobile ? "8px" : "4px 8px",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: isMobile ? "8px" : "6px",
                                      fontSize: isMobile ? "14px" : "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      outline: "none",
                                      height: isMobile ? "44px" : "34px",
                                      width: isMobile ? "100%" : "120px",
                                      flex: isMobile ? "1" : "0 0 auto"
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = "#0ea5e9";
                                      e.target.style.boxShadow = "0 0 0 2px rgba(14, 165, 233, 0.1)";
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = "#e5e7eb";
                                      e.target.style.boxShadow = "none";
                                    }}
                                    min={boardFromDate || undefined}
                                  />
                                </div>
                                <button
                                  onClick={() => {
                                    const today = getTodayDate();
                                    setBoardFromDate(today);
                                    setBoardToDate(today);
                                  }}
                                  style={{
                                    padding: "4px 10px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    color: "#6b7280",
                                    backgroundColor: "#f9fafb",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                    whiteSpace: "nowrap",
                                    height: "34px"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                                    e.currentTarget.style.borderColor = "#9ca3af";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#f9fafb";
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                  }}
                                  title="Filter to today"
                                >
                                  Today
                                </button>
                                <button
                                  onClick={() => {
                                    setBoardFromDate("");
                                    setBoardToDate("");
                                  }}
                                  style={{
                                    padding: "4px 10px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    color: "#6b7280",
                                    backgroundColor: "#f9fafb",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                    whiteSpace: "nowrap",
                                    height: "34px"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                                    e.currentTarget.style.borderColor = "#9ca3af";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#f9fafb";
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                  }}
                                  title="Show all dates"
                                >
                                  All
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Center: Project Name (Title) - Bold and Highlighted */}
                          <div style={{ flex: "1 1 auto", display: "flex", justifyContent: "center", minWidth: 0 }}>
                            <span style={{
                              color: "#4b5563",
                              fontSize: "17px",
                              fontWeight: "700",
                              letterSpacing: "-0.01em",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                              backgroundColor: "#f3f4f6",
                              padding: "6px 16px",
                              borderRadius: "8px",
                              border: "1px solid #e5e7eb"
                            }}>
                              {selectedProjectName}
                            </span>
                          </div>

                          {/* Right: Search Box */}
                          <div style={{ flex: "0 0 auto" }}>
                            {/* Search Box - Only show for Chat/Board views */}
                            {(projectViewMode === "chat" || projectViewMode === "board") && (
                              <div style={{ position: "relative", minWidth: "200px", maxWidth: "300px" }}>
                                <input
                                  type="text"
                                  placeholder="Search..."
                                  style={{
                                    fontSize: "13px",
                                    height: "32px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    paddingLeft: "32px",
                                    paddingRight: "8px",
                                    backgroundColor: "#f9fafb",
                                    outline: "none",
                                    width: "100%",
                                    transition: "all 0.15s ease"
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = "#9ca3af";
                                    e.target.style.backgroundColor = "#ffffff";
                                    e.target.style.boxShadow = "0 0 0 3px rgba(156, 163, 175, 0.1)";
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = "#e5e7eb";
                                    e.target.style.backgroundColor = "#f9fafb";
                                    e.target.style.boxShadow = "none";
                                  }}
                                />
                                <i
                                  className="bi bi-search"
                                  style={{
                                    position: "absolute",
                                    left: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#94a3b8",
                                    fontSize: "14px"
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Chat View Content */}
                    {projectViewMode === "chat" && (
                      <EmbeddedChatView
                        projectId={selectedProjectId}
                        projectName={selectedProjectName}
                      />
                    )}

                    {/* Board View Content */}
                    {projectViewMode === "board" && (
                      <EmbeddedBoardView
                        projectId={selectedProjectId}
                        projectName={selectedProjectName}
                        filterUser={selectedBoardUser}
                        fromDate={boardFromDate}
                        toDate={boardToDate}
                      />
                    )}

                  </div>
                ) : (
                  <>
                    {/* CREATE PROJECT */}
                    {userRole === "admin" && (
                      <>
                        {/* Desktop View */}
                        {!isMobile && (
                          <div className="mb-3 mb-md-4 p-2 p-md-3 shadow-sm bg-white create-project-box">
                            <h5 className="mb-2 mb-md-3 create-project-title">Create New Project</h5>

                            <div className="d-flex align-items-center mb-2 mb-md-3 create-project-input-group">
                              <input
                                type="text"
                                value={projectName}
                                onChange={(e) => setProjectName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && addProject()}
                                className="form-control add-project-input"
                                placeholder="Enter project name..."
                                style={{
                                  flex: 1,
                                  padding: "12px 16px",
                                  fontSize: "16px",
                                  border: "2px solid #e2e8f0",
                                  borderRadius: "12px",
                                  transition: "all 0.3s ease",
                                  outline: "none"
                                }}
                                onFocus={(e) => {
                                  e.currentTarget.style.borderColor = "#667eea";
                                  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
                                }}
                                onBlur={(e) => {
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.boxShadow = "none";
                                }} />

                              <button
                                className="btn ms-2 ms-md-3 text-white add-project-btn"
                                onClick={addProject}
                                style={{
                                  backgroundColor: "#0ea5e9",
                                  borderRadius: "50%",
                                  width: "56px",
                                  height: "56px",
                                  flexShrink: 0,
                                  border: "none",
                                  boxShadow: "0 2px 4px rgba(14, 165, 233, 0.2)",
                                  fontSize: "28px",
                                  transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#0284c7";
                                  e.currentTarget.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#0ea5e9";
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Mobile View - WhatsApp-style Bottom Input */}
                        {isMobile && (
                          <div
                            className="whatsapp-input-bar"
                            style={{
                              position: "fixed",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              backgroundColor: "#f0f0f0",
                              padding: "8px 12px",
                              borderTop: "1px solid #e0e0e0",
                              zIndex: 1000,
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              boxShadow: "0 -2px 10px rgba(0,0,0,0.1)"
                            }}
                          >
                            <input
                              type="text"
                              value={projectName}
                              onChange={(e) => setProjectName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  addProject();
                                }
                              }}
                              className="form-control whatsapp-input-field"
                              placeholder="Type project name..."
                              style={{
                                flex: 1,
                                borderRadius: "20px",
                                border: "1px solid #e0e0e0",
                                padding: "10px 16px",
                                fontSize: "15px",
                                backgroundColor: "white",
                                outline: "none"
                              }}
                            />
                            <button
                              onClick={addProject}
                              className="btn btn-success whatsapp-send-btn"
                              disabled={!projectName.trim()}
                              style={{
                                width: "44px",
                                height: "44px",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                                backgroundColor: projectName.trim() ? "#0ea5e9" : "#ccc",
                                border: "none",
                                cursor: projectName.trim() ? "pointer" : "not-allowed",
                                boxShadow: projectName.trim() ? "0 2px 4px rgba(14, 165, 233, 0.2)" : "none"
                              }}
                            >
                              <i className="bi bi-send-fill" style={{ fontSize: "18px", color: "white" }}></i>
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* PROJECT LIST - Responsive Grid */}
                    {projects.length > 0 && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile
                            ? "1fr"
                            : "repeat(auto-fill, minmax(300px, 1fr))",
                          gap: isMobile ? "16px" : "24px",
                          marginTop: "16px"
                        }}
                      >
                        {projects.map((p) => (
                          <div
                            key={p.id}
                            style={{
                              width: "100%",
                              minWidth: 0
                            }}
                          >
                            <div
                              className="project-card-responsive"
                              style={{
                                backgroundColor: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: isMobile ? "16px" : "12px",
                                padding: isMobile ? "20px" : "20px",
                                transition: "all 0.2s ease",
                                position: "relative",
                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column"
                              }}
                              onMouseEnter={(e) => {
                                if (!isMobile) {
                                  e.currentTarget.style.borderColor = "#d1d5db";
                                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isMobile) {
                                  e.currentTarget.style.borderColor = "#e5e7eb";
                                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                                  e.currentTarget.style.transform = "translateY(0)";
                                }
                              }}
                            >
                              {editId === p.id ? (
                                <input
                                  className="form-control project-edit-input mb-3"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  autoFocus
                                  style={{
                                    fontSize: isMobile ? "18px" : "16px",
                                    padding: isMobile ? "14px" : "12px",
                                    borderRadius: "8px",
                                    border: "2px solid #0ea5e9",
                                    width: "100%"
                                  }}
                                />
                              ) : (
                                <h6 style={{
                                  color: "#111827",
                                  fontSize: isMobile ? "18px" : "16px",
                                  fontWeight: "700",
                                  marginBottom: isMobile ? "16px" : "16px",
                                  paddingRight: userRole === "admin" && !isMobile ? "80px" : (userRole === "admin" && isMobile ? "60px" : "0"),
                                  lineHeight: "1.4",
                                  wordBreak: "break-word"
                                }}>
                                  {p.name}
                                </h6>
                              )}

                              {userRole === "admin" && (
                                <div className="mb-3" style={{ flex: "1" }}>
                                  <div className="text-muted mb-2" style={{ fontSize: isMobile ? "14px" : "13px", fontWeight: "600", color: "#6b7280" }}>
                                    Assigned:
                                  </div>
                                  <div className="d-flex flex-wrap gap-1" style={{ minHeight: "24px" }}>
                                    {p.users && p.users.length > 0 ? (
                                      p.users.map((uid) => {
                                        const user = allUsers.find((u) => u.id === uid);
                                        const displayName = user ? (user.username || user.email) : "Unknown";
                                        return (
                                          <span
                                            key={uid}
                                            className="badge"
                                            style={{
                                              backgroundColor: "#f1f5f9",
                                              color: "#475569",
                                              fontSize: "11px",
                                              fontWeight: "500",
                                              padding: "4px 10px",
                                              borderRadius: "12px",
                                              border: "1px solid #e2e8f0"
                                            }}
                                          >
                                            {displayName}
                                          </span>
                                        );
                                      })
                                    ) : (
                                      <span
                                        className="badge"
                                        style={{
                                          backgroundColor: "#f1f5f9",
                                          color: "#94a3b8",
                                          fontSize: "11px",
                                          fontWeight: "500",
                                          padding: "4px 10px",
                                          borderRadius: "12px",
                                          border: "1px solid #e2e8f0"
                                        }}
                                      >
                                        None
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {userRole === "admin" && (
                                <div className="mt-auto d-flex flex-wrap justify-content-between gap-1 project-actions"
                                  style={{
                                    position: "absolute",
                                    top: isMobile ? "12px" : "10px",
                                    right: isMobile ? "12px" : "10px",
                                    display: "flex",
                                    gap: isMobile ? "8px" : "6px",
                                    zIndex: 10
                                  }}>
                                  {editId === p.id ? (
                                    <button
                                      className="btn btn-sm btn-primary project-action-btn"
                                      onClick={() => updateProject(p.id)}
                                      style={{
                                        padding: isMobile ? "8px 16px" : "4px 12px",
                                        fontSize: isMobile ? "14px" : "12px",
                                        minHeight: isMobile ? "40px" : "auto"
                                      }}
                                    >
                                      Save
                                    </button>
                                  ) : (

                                    <button
                                      className=" text-end btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center task-action-btn"
                                      onClick={() => { setEditId(p.id); setEditName(p.name || ""); }}
                                      style={{
                                        borderRadius: "50%",
                                        width: isMobile ? "40px" : "32px",
                                        height: isMobile ? "40px" : "32px",
                                        padding: 0,
                                        minWidth: isMobile ? "40px" : "32px"
                                      }}
                                      title="Edit"
                                    >
                                      <i className="bi bi-pencil-square" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
                                    </button>

                                  )}

                                  <button
                                    className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center task-action-btn"
                                    onClick={() => deleteProject(p.id)}
                                    style={{
                                      borderRadius: "50%",
                                      width: isMobile ? "40px" : "32px",
                                      height: isMobile ? "40px" : "32px",
                                      padding: 0,
                                      minWidth: isMobile ? "40px" : "32px"
                                    }}
                                    title="Delete"
                                  >
                                    <i className="bi bi-trash3" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
                                  </button>

                                </div>
                              )}
                              {/* Action Buttons - Improved Layout */}
                              <div
                                className="d-flex gap-2"
                                style={{
                                  flexWrap: isMobile ? "wrap" : "nowrap",
                                  width: "100%",
                                  marginTop: "auto",
                                  paddingTop: "16px"
                                }}
                              >
                                <button
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor: "#111827",
                                    color: "#fff",
                                    position: "relative",
                                    border: "none",
                                    borderRadius: isMobile ? "10px" : "8px",
                                    padding: isMobile ? "14px 20px" : "10px 16px",
                                    fontSize: isMobile ? "16px" : "14px",
                                    fontWeight: "600",
                                    flex: isMobile ? "1 1 calc(50% - 4px)" : "1 1 auto",
                                    minHeight: isMobile ? "48px" : "40px",
                                    minWidth: isMobile ? "0" : "auto",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
                                  }}
                                  onClick={() => handleView(p.id, p.name)}
                                  onMouseEnter={(e) => {
                                    if (!isMobile) {
                                      e.currentTarget.style.backgroundColor = "#374151";
                                      e.currentTarget.style.transform = "translateY(-1px)";
                                      e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isMobile) {
                                      e.currentTarget.style.backgroundColor = "#111827";
                                      e.currentTarget.style.transform = "translateY(0)";
                                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                                    }
                                  }}
                                >
                                  Chat
                                  {(() => {
                                    const notificationCount = projectNotificationCounts[p.id] || 0;
                                    const mentionCount = projectMentionCounts[p.id] || 0;
                                    const hasNotifications = notificationCount > 0 || mentionCount > 0;

                                    // Enhanced debug logging
                                    console.log(`\n🎯 === CHAT BUTTON RENDER for ${p.name} (${p.id}) ===`);
                                    console.log(`   projectNotificationCounts[${p.id}] =`, notificationCount);
                                    console.log(`   projectMentionCounts[${p.id}] =`, mentionCount);
                                    console.log(`   hasNotifications =`, hasNotifications);
                                    console.log(`   All notification counts:`, projectNotificationCounts);
                                    console.log(`   All mention counts:`, projectMentionCounts);

                                    if (!hasNotifications) {
                                      console.log(`   ❌ SKIPPING BADGE - No notifications`);
                                      return null;
                                    }

                                    const badgeText = mentionCount > 0 ? '@' : notificationCount;
                                    const badgeColor = mentionCount > 0 ? '#f59e0b' : '#ef4444';
                                    console.log(`   ✅ RENDERING BADGE: "${badgeText}" with color ${badgeColor}`);


                                    console.log(`  ✅ Rendering badge for project ${p.name}: ${mentionCount > 0 ? '@' : notificationCount}`);

                                    return (
                                      <span
                                        style={{
                                          position: "absolute",
                                          top: "-6px",
                                          right: "-6px",
                                          backgroundColor: mentionCount > 0 ? "#f59e0b" : "#ef4444",
                                          color: "white",
                                          borderRadius: "10px",
                                          minWidth: mentionCount > 0 ? "20px" : "18px",
                                          height: "18px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: mentionCount > 0 ? "12px" : "10px",
                                          fontWeight: "700",
                                          padding: mentionCount > 0 ? "0 6px" : "0 4px",
                                          zIndex: 1000,
                                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                          border: "2px solid white",
                                          pointerEvents: "none",
                                          lineHeight: "1"
                                        }}
                                      >
                                        {mentionCount > 0
                                          ? "@"
                                          : (notificationCount > 99
                                            ? "99+"
                                            : notificationCount)}
                                      </span>
                                    );
                                  })()}
                                </button>

                                <button
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor: "#f3f4f6",
                                    color: "#374151",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: isMobile ? "10px" : "8px",
                                    padding: isMobile ? "14px 20px" : "10px 16px",
                                    fontSize: isMobile ? "16px" : "14px",
                                    fontWeight: "600",
                                    flex: isMobile ? "1 1 calc(50% - 4px)" : "1 1 auto",
                                    minHeight: isMobile ? "48px" : "40px",
                                    minWidth: isMobile ? "0" : "auto",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)"
                                  }}
                                  onClick={() => handleBoardView(p.id, p.name)}
                                  onMouseEnter={(e) => {
                                    if (!isMobile) {
                                      e.currentTarget.style.backgroundColor = "#e5e7eb";
                                      e.currentTarget.style.borderColor = "#d1d5db";
                                      e.currentTarget.style.transform = "translateY(-1px)";
                                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isMobile) {
                                      e.currentTarget.style.backgroundColor = "#f3f4f6";
                                      e.currentTarget.style.borderColor = "#e5e7eb";
                                      e.currentTarget.style.transform = "translateY(0)";
                                      e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.05)";
                                    }
                                  }}
                                >
                                  Board
                                </button>

                                {userRole === "admin" && (
                                  <button
                                    className="btn btn-sm"
                                    style={{
                                      backgroundColor: "#0ea5e9",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: isMobile ? "10px" : "8px",
                                      padding: isMobile ? "14px 20px" : "10px 16px",
                                      fontSize: isMobile ? "16px" : "14px",
                                      fontWeight: "600",
                                      width: isMobile ? "100%" : "auto",
                                      minHeight: isMobile ? "48px" : "40px",
                                      transition: "all 0.2s ease",
                                      boxShadow: "0 2px 4px rgba(14, 165, 233, 0.2)",
                                      marginTop: isMobile ? "8px" : "0"
                                    }}
                                    onClick={() => openAssignModal(p)}
                                    onMouseEnter={(e) => {
                                      if (!isMobile) {
                                        e.currentTarget.style.backgroundColor = "#0284c7";
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                        e.currentTarget.style.boxShadow = "0 4px 8px rgba(14, 165, 233, 0.3)";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isMobile) {
                                        e.currentTarget.style.backgroundColor = "#0ea5e9";
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 2px 4px rgba(14, 165, 233, 0.2)";
                                      }
                                    }}
                                  >
                                    Assign
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {projects.length === 0 && (
                      <div
                        className="text-center text-muted"
                        style={{
                          padding: isMobile ? "40px 20px" : "60px 20px",
                          fontSize: isMobile ? "16px" : "18px",
                          color: "#6b7280"
                        }}
                      >
                        No projects created yet.
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* USERS PAGE */}
            {activePage === "users" && (
              <div
                className="p-2 p-md-3 users-page-container"
                style={{
                  maxHeight: userRole === "admin" ? "calc(100vh - 200px)" : "calc(100vh - 150px)",
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingBottom: userRole === "admin" ? "70px" : "20px"
                }}
              >
                {/* ADD NEW USER BUTTON - Desktop only (hidden on mobile/tablet) */}
                {userRole === "admin" && !isMobile && (
                  <div className="mb-3 mb-md-4 d-flex justify-content-end">
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowUserModal(true)}
                      style={{
                        backgroundColor: "#0ea5e9",
                        border: "none",
                        padding: "10px 24px",
                        fontSize: "14px",
                        fontWeight: "500",
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(14, 165, 233, 0.2)",
                        transition: "all 0.2s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#0284c7";
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow = "0 4px 8px rgba(14, 165, 233, 0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#0ea5e9";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 4px rgba(14, 165, 233, 0.2)";
                      }}
                    >
                      <i className="bi bi-person-plus-fill me-2"></i>
                      Add New User
                    </button>
                  </div>
                )}

                {/* ADMINS TABLE */}
                {userRole === "admin" && (
                  <>
                    <h3 className="mb-2 mb-md-3 users-page-title">Admins</h3>
                    {allUsers.filter((user) => user.role === "admin").length === 0 ? (
                      <p className="mb-4">No admins found.</p>
                    ) : (
                      <div className="table-responsive mb-4">
                        <table className="table table-bordered users-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allUsers
                              .filter((user) => user.role === "admin")
                              .map((user) => (
                                <tr key={user.id}>
                                  <td>{user.username}</td>
                                  <td>{user.email}</td>
                                  <td>{user.role}</td>
                                  <td>
                                    <button className="btn btn-danger btn-sm user-delete-btn" onClick={() => deleteUser(user.id)}>
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {/* USERS TABLE */}
                <h3 className="mb-2 mb-md-3 users-page-title">Users</h3>
                {allUsers.filter((user) => user.role !== "admin").length === 0 ? (
                  <p>No users found.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered users-table">
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allUsers
                          .filter((user) => user.role !== "admin")
                          .map((user) => (
                            <tr key={user.id}>
                              <td>{user.username}</td>
                              <td>{user.email}</td>
                              <td>{user.role}</td>
                              <td>
                                <button className="btn btn-danger btn-sm user-delete-btn" onClick={() => deleteUser(user.id)}>
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* WhatsApp-style Bottom Input Bar for Users Page - Only for Admin and Mobile/Tablet (max-width: 992px) */}
            {activePage === "users" && userRole === "admin" && isMobile && (
              <div
                className="whatsapp-input-bar"
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "white",
                  borderTop: "1px solid #ddd",
                  padding: "8px",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 -2px 10px rgba(0,0,0,0.1)"
                }}
              >
                <input
                  type="text"
                  value={newUserInput}
                  onChange={(e) => setNewUserInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newUserInput.trim()) {
                      // Parse input: format "username email" or "email"
                      const parts = newUserInput.trim().split(/\s+/);
                      if (parts.length >= 2) {
                        setNewUsername(parts[0]);
                        setNewEmail(parts.slice(1).join(' '));
                      } else if (parts.length === 1) {
                        // If only one part, assume it's email
                        setNewEmail(parts[0]);
                        setNewUsername(parts[0].split('@')[0]); // Use email prefix as username
                      }
                      setShowUserModal(true);
                      setNewUserInput("");
                    }
                  }}
                  className="form-control whatsapp-input-field"
                  placeholder="Add user…"
                  style={{
                    flex: 1,
                    borderRadius: "20px",
                    border: "1px solid #e0e0e0",
                    padding: "10px 16px",
                    fontSize: "15px",
                    backgroundColor: "white",
                    outline: "none"
                  }}
                />
                <button
                  onClick={() => {
                    if (newUserInput.trim()) {
                      // Parse input: format "username email" or "email"
                      const parts = newUserInput.trim().split(/\s+/);
                      if (parts.length >= 2) {
                        setNewUsername(parts[0]);
                        setNewEmail(parts.slice(1).join(' '));
                      } else if (parts.length === 1) {
                        // If only one part, assume it's email
                        setNewEmail(parts[0]);
                        setNewUsername(parts[0].split('@')[0]); // Use email prefix as username
                      }
                      setShowUserModal(true);
                      setNewUserInput("");
                    }
                  }}
                  className="btn btn-success whatsapp-send-btn"
                  disabled={!newUserInput.trim()}
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    backgroundColor: newUserInput.trim() ? "#2a8c7b" : "#ccc",
                    border: "none",
                    cursor: newUserInput.trim() ? "pointer" : "not-allowed"
                  }}
                >
                  <i className="bi bi-plus-lg" style={{ fontSize: "20px", color: "white" }}></i>
                </button>
              </div>
            )}

            {/* LEAVES PAGE */}
            {activePage === "leaves" && (
              <div className="container-fluid p-3 p-md-5" style={{ backgroundColor: "#fafaf9", minHeight: "100%", fontFamily: "'Inter', sans-serif" }}>
                <div className="mb-5">
                  <h2 className="fw-bold mb-1" style={{ fontSize: isMobile ? "24px" : "32px", color: "#1e293b", letterSpacing: "-1px" }}>
                    {userRole === "admin" ? "All Leave Requests" : "Apply Leave & My Leaves"}
                  </h2>
                  <p className="text-muted mb-0" style={{ fontSize: "14px", letterSpacing: "0.5px" }}>Submit your time off requests and track their status.</p>
                </div>

                {/* Apply Form Card */}
                <div className="card border-0 rounded-4 shadow-sm mb-5 transition-all overflow-hidden" style={{ backgroundColor: "#fff", border: "1px solid #f1f5f9" }}>
                  <div className="card-header bg-white border-0 px-4 pt-4 pb-0 d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.3px" }}>New Request</h5>
                    {userRole !== "admin" && (
                      <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill" style={{ backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", fontSize: "13px", fontWeight: "600" }}>
                        <i className="bi bi-wallet2"></i>
                        <span>Balance: {calculateLeaveBalance(userCreatedAt, myLeaves)} Days</span>
                      </div>
                    )}
                  </div>
                  <div className="card-body p-4">
                    <form onSubmit={handleLeaveSubmit}>
                      <div className="row g-4">
                        <div className="col-12 col-md-4">
                          <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Leave Type</label>
                          <select 
                            className="form-select rounded-3 shadow-none border-0" 
                            value={leaveType} 
                            onChange={(e) => setLeaveType(e.target.value)}
                            style={{ backgroundColor: "#f8fafc", padding: "12px 16px", fontSize: "14px", cursor: "pointer", color: "#334155" }}
                            required
                          >
                            <option value="Casual">Casual Leave</option>
                            <option value="Sick">Sick Leave</option>
                            <option value="Paid">Paid Leave</option>
                            <option value="Half Day">Half Day Leave</option>
                          </select>
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>From Date</label>
                          <input 
                            type="date" 
                            className="form-control rounded-3 shadow-none border-0" 
                            value={leaveFromDate} 
                            onChange={(e) => setLeaveFromDate(e.target.value)}
                            style={{ backgroundColor: "#f8fafc", padding: "12px 16px", fontSize: "14px", cursor: "pointer", color: "#334155" }}
                            required
                          />
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>To Date</label>
                          <input 
                            type="date" 
                            className="form-control rounded-3 shadow-none border-0" 
                            value={leaveToDate} 
                            onChange={(e) => setLeaveToDate(e.target.value)}
                            style={{ backgroundColor: "#f8fafc", padding: "12px 16px", fontSize: "14px", cursor: "pointer", color: "#334155" }}
                            required
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Reason</label>
                          <textarea 
                            className="form-control rounded-3 shadow-none border-0" 
                            rows="2" 
                            value={leaveReason}
                            onChange={(e) => setLeaveReason(e.target.value)}
                            placeholder="Briefly explain the reason for your leave..."
                            style={{ backgroundColor: "#f8fafc", padding: "16px", fontSize: "14px", color: "#334155", resize: "none" }}
                            required
                          ></textarea>
                        </div>
                        <div className="col-12 mt-4 d-flex justify-content-end">
                          <button 
                            type="submit" 
                            className="btn rounded-pill fw-semibold px-5 transition-all d-flex align-items-center justify-content-center gap-2" 
                            disabled={submittingLeave}
                            style={{ 
                              backgroundColor: "#0ea5e9", color: "#fff", border: "none", fontSize: "15px", height: "48px",
                              boxShadow: "0 4px 12px rgba(14, 165, 233, 0.25)"
                            }}
                            onMouseEnter={(e) => { if(!submittingLeave) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(14, 165, 233, 0.4)"; } }}
                            onMouseLeave={(e) => { if(!submittingLeave) { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(14, 165, 233, 0.25)"; } }}
                          >
                            {submittingLeave ? (
                              <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Processing...</>
                            ) : (
                              <>Submit Request <i className="bi bi-arrow-right"></i></>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-3 mb-4">
                  <h4 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>My Leave History</h4>
                  <div style={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }}></div>
                </div>

                {/* My Leaves Data Table */}
                <div className="bg-white rounded-4 shadow-sm overflow-hidden" style={{ border: "1px solid #f1f5f9" }}>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0" style={{ minWidth: "800px" }}>
                      <thead>
                        <tr>
                          <th className="border-0 bg-light text-muted fw-semibold py-3 px-4" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Leave Details</th>
                          <th className="border-0 bg-light text-muted fw-semibold py-3 px-4" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Duration</th>
                          <th className="border-0 bg-light text-muted fw-semibold py-3 px-4" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px", width: "35%" }}>Reason</th>
                          <th className="border-0 bg-light text-muted fw-semibold py-3 px-4" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody style={{ borderTop: "none" }}>
                        {myLeaves.length > 0 ? (
                          myLeaves.map(leave => (
                            <tr key={leave.id} className="transition-all" style={{ borderBottom: "1px solid #f8fafc" }}>
                              <td className="py-4 px-4 border-0">
                                <span className="badge rounded-pill" style={{ backgroundColor: "#f1f5f9", color: "#475569", fontWeight: "600", border: "1px solid #e2e8f0", padding: "6px 12px" }}>
                                  {leave.leaveType} Leave
                                </span>
                              </td>
                              <td className="py-4 px-4 border-0">
                                <div className="d-flex flex-column gap-1">
                                  <div className="text-dark fw-medium" style={{ fontSize: "13px" }}>{new Date(leave.fromDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                                  <div className="text-muted" style={{ fontSize: "12px" }}>to {new Date(leave.toDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                                </div>
                              </td>
                              <td className="py-4 px-4 border-0">
                                <div className="text-muted text-truncate" style={{ fontSize: "13px", maxWidth: "300px" }} title={leave.reason}>
                                  "{leave.reason}"
                                </div>
                              </td>
                              <td className="py-4 px-4 border-0">
                                {leave.status === "approved" ? (
                                  <div className="d-inline-flex align-items-center gap-2" style={{ backgroundColor: "rgba(34, 197, 94, 0.08)", color: "#15803d", padding: "6px 14px", borderRadius: "20px", fontWeight: "600", fontSize: "12px", border: "1px solid rgba(34, 197, 94, 0.2)" }}>
                                    <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#16a34a" }}></div> Approved
                                  </div>
                                ) : leave.status === "rejected" ? (
                                  <div className="d-inline-flex align-items-center gap-2" style={{ backgroundColor: "rgba(239, 68, 68, 0.08)", color: "#b91c1c", padding: "6px 14px", borderRadius: "20px", fontWeight: "600", fontSize: "12px", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                                    <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#dc2626" }}></div> Rejected
                                  </div>
                                ) : (
                                  <div className="d-inline-flex align-items-center gap-2" style={{ backgroundColor: "rgba(245, 158, 11, 0.08)", color: "#b45309", padding: "6px 14px", borderRadius: "20px", fontWeight: "600", fontSize: "12px", border: "1px solid rgba(245, 158, 11, 0.2)" }}>
                                    <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#d97706" }}></div> Pending
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-5 border-0">
                              <div className="d-flex flex-column justify-content-center align-items-center">
                                <div className="rounded-circle d-flex justify-content-center align-items-center mb-3" style={{ width: "64px", height: "64px", backgroundColor: "#f8fafc" }}>
                                  <i className="bi bi-inbox text-secondary" style={{ fontSize: "24px", opacity: 0.5 }}></i>
                                </div>
                                <h6 className="fw-semibold text-secondary mb-1">No Leave History</h6>
                                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>You haven't requested any leaves yet.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ADMIN ALL LEAVES PAGE */}
            {activePage === "all_leaves" && (
              <div style={{ margin: "-1rem" }}>
                <AdminLeaves />
              </div>
            )}

            {/* ATTENDANCE RECORDS PAGE */}
            {activePage === "attendance" && (
              <div className="p-2 p-md-4">
                <div className="mb-3 mb-md-4">
                  <h2 className="fw-bold" style={{ fontSize: isMobile ? "20px" : "24px" }}>
                    {userRole === "admin" ? "Attendance Records" : "My Attendance Records"}
                  </h2>
                  <div 
                    className="d-flex align-items-center bg-light rounded-pill p-1 shadow-sm mt-3 mt-md-0" 
                    style={{ border: "1px solid #e2e8f0", display: "inline-flex" }}
                  >
                    <button 
                      type="button" 
                      className={`btn btn-sm rounded-pill d-flex align-items-center justify-content-center transition-all px-3 py-1 m-0 ${attendanceViewMode === 'table' ? 'bg-white shadow-sm text-primary fw-bold' : 'text-secondary fw-medium border-0 bg-transparent'}`}
                      onClick={() => setAttendanceViewMode('table')}
                      style={{ fontSize: "14px", minWidth: "110px" }}
                    >
                      <i className="bi bi-table me-2"></i> Table
                    </button>
                    <button 
                      type="button" 
                      className={`btn btn-sm rounded-pill d-flex align-items-center justify-content-center transition-all px-3 py-1 m-0 ${attendanceViewMode === 'calendar' ? 'bg-white shadow-sm text-primary fw-bold' : 'text-secondary fw-medium border-0 bg-transparent'}`}
                      onClick={() => setAttendanceViewMode('calendar')}
                      style={{ fontSize: "14px", minWidth: "120px" }}
                    >
                      <i className="bi bi-calendar-grid-3x3 me-2"></i> Calendar
                    </button>
                  </div>
                </div>

                {/* Common Filters for both Table and Calendar when Admin */}
                {userRole === "admin" && attendanceViewMode === "calendar" && (
                  <div className="mb-4">
                    <label className="form-label fw-semibold text-secondary" style={{ fontSize: isMobile ? "13px" : "14px", letterSpacing: "0.3px", textTransform: "uppercase" }}>
                      Select Employee
                    </label>
                    <select
                      className="form-select shadow-sm"
                      value={filterEmail}
                      onChange={(e) => setFilterEmail(e.target.value)}
                      style={{ 
                        fontSize: "15px", 
                        maxWidth: "400px", 
                        borderRadius: "10px", 
                        border: "1px solid #cbd5e1",
                        padding: "10px 16px",
                        color: "#334155"
                      }}
                    >
                      <option value="">-- Choose an Employee --</option>
                      {allUsers.map(user => (
                        <option key={user.id} value={user.email}>
                          {user.username || user.email?.split('@')[0] || "User"} ({user.email})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted d-block mt-2" style={{fontSize: "12px"}}>
                      Select an employee from the dropdown to view their detailed attendance and leave calendar.
                    </small>
                  </div>
                )}

                {attendanceViewMode === "calendar" ? (
                  userRole === "admin" && !filterEmail ? (
                    <div>
                      <div className="alert alert-info shadow-sm d-flex align-items-center mb-4" role="alert">
                        <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                        <div>
                          <h6 className="alert-heading mb-1 fw-bold">Global Company Calendar</h6>
                          <p className="mb-0" style={{ fontSize: "14px" }}>Click on any date below to mark it as a holiday or remove an existing holiday. Holidays will be reflected for all users.</p>
                        </div>
                      </div>
                      <AttendanceCalendar 
                        attendanceRecords={[]}
                        leaveRecords={[]}
                        holidays={holidays}
                        isMobile={isMobile}
                        targetUserName="Global Company"
                        onDateClick={handleDateClick}
                        onMonthChange={setAttendanceCalendarMonth}
                      />
                    </div>
                  ) : (
                    <div>
                      {userRole === "admin" && filterEmail && (
                        <div className="d-flex justify-content-end mb-3">
                          <button 
                            className="btn btn-sm btn-success shadow-sm rounded-pill px-3 py-2 fw-semibold d-flex align-items-center"
                            onClick={handleCompleteTodayHours}
                            style={{ fontSize: "14px", letterSpacing: "0.3px", transition: "all 0.2s" }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
                            onMouseLeave={(e) => e.currentTarget.style.transform = "none"}
                          >
                            <i className="bi bi-check-circle-fill me-2" style={{ fontSize: "16px" }}></i> 
                            Complete Today's Hours (8h 30m)
                          </button>
                        </div>
                      )}
                      <AttendanceCalendar 
                        attendanceRecords={attendanceRecords} 
                        leaveRecords={userRole === "admin" ? adminSelectedUserLeaves.filter(l => l.status === 'approved') : myLeaves.filter(l => l.status === 'approved')} 
                        holidays={holidays}
                        isMobile={isMobile}
                        targetUserCreatedAt={userRole === "admin" && filterEmail ? allUsers.find(u => u.email === filterEmail)?.createdAt : userCreatedAt}
                        targetUserName={userRole === "admin" && filterEmail ? allUsers.find(u => u.email === filterEmail)?.username : userName}
                        onMonthChange={setAttendanceCalendarMonth}
                        onDateClick={userRole === "admin" ? handleDateClick : undefined}
                      />
                    </div>
                  )
                ) : (
                  <>
                    {/* Filters */}
                <div className="row mb-3 mb-md-4">
                  <div className="col-12 col-md-6 mb-2 mb-md-0">
                    <label className="form-label fw-bold" style={{ fontSize: isMobile ? "14px" : "16px" }}>Filter by Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      style={{ fontSize: isMobile ? "14px" : "16px", borderRadius: "8px", border: "1px solid #cbd5e1" }}
                    />
                  </div>

                  {/* Email Filter - Only for Admin */}
                  {userRole === "admin" && (
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold" style={{ fontSize: isMobile ? "14px" : "16px" }}>Filter by User Email</label>
                      <select
                        className="form-select shadow-sm"
                        value={filterEmail}
                        onChange={(e) => setFilterEmail(e.target.value)}
                        style={{ fontSize: "14px", borderRadius: "8px", border: "1px solid #cbd5e1", color: "#334155" }}
                      >
                        <option value="">All Employees</option>
                        {allUsers.map(user => (
                          <option key={user.id} value={user.email}>
                            {user.username || user.email?.split('@')[0] || "User"} ({user.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Work Hours Statistics Card */}
                {!attendanceLoading && (userRole !== "admin" || filterEmail) && (
                  <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                    <div className="card-body p-4">
                      <h5 className="card-title fw-bold mb-3" style={{ color: "#1e293b" }}>
                        Work Hours Summary {filterEmail ? `for ${filterEmail}` : ""}
                      </h5>

                      {(() => {
                        const stats = calculateWorkHoursStats(attendanceRecords, filterEmail);
                        return (
                          <div className="row g-3">
                            {/* This Month */}
                            <div className="col-12 col-md-4">
                              <div className="p-3 rounded-3" style={{ backgroundColor: "#eff6ff", border: "1px solid #dbeafe" }}>
                                <div className="d-flex align-items-center mb-1">
                                  <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-2 d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px" }}>
                                    <i className="bi bi-calendar-month text-primary" style={{ fontSize: "16px" }}></i>
                                  </div>
                                  <span className="text-secondary fw-medium" style={{ fontSize: "13px" }}>This Month</span>
                                </div>
                                <div className="fw-bold text-primary" style={{ fontSize: "24px", marginLeft: "40px" }}>
                                  {stats.thisMonthFormatted}
                                </div>
                              </div>
                            </div>

                            {/* This Week */}
                            <div className="col-12 col-md-4">
                              <div className="p-3 rounded-3" style={{ backgroundColor: "#f0fdf4", border: "1px solid #dcfce7" }}>
                                <div className="d-flex align-items-center mb-1">
                                  <div className="rounded-circle bg-success bg-opacity-10 p-2 me-2 d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px" }}>
                                    <i className="bi bi-calendar-week text-success" style={{ fontSize: "16px" }}></i>
                                  </div>
                                  <span className="text-secondary fw-medium" style={{ fontSize: "13px" }}>This Week</span>
                                </div>
                                <div className="fw-bold text-success" style={{ fontSize: "24px", marginLeft: "40px" }}>
                                  {stats.thisWeekFormatted}
                                </div>
                              </div>
                            </div>

                            {/* Yesterday */}
                            <div className="col-12 col-md-4">
                              <div className="p-3 rounded-3" style={{ backgroundColor: "#fffbeb", border: "1px solid #fef3c7" }}>
                                <div className="d-flex align-items-center mb-1">
                                  <div className="rounded-circle bg-warning bg-opacity-10 p-2 me-2 d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px" }}>
                                    <i className="bi bi-clock-history text-warning" style={{ fontSize: "16px" }}></i>
                                  </div>
                                  <span className="text-secondary fw-medium" style={{ fontSize: "13px" }}>Yesterday</span>
                                </div>
                                <div className="fw-bold text-warning" style={{ fontSize: "24px", marginLeft: "40px" }}>
                                  {stats.yesterdayFormatted}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {attendanceLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : isMobile ? (
                  /* Mobile Card View */
                  <div className="row g-3">
                    {displayedAttendanceRecords.length === 0 ? (
                      <div className="col-12">
                        <div className="text-center py-4">
                          <p className="text-muted">No attendance records found</p>
                        </div>
                      </div>
                    ) : (
                      displayedAttendanceRecords.map((record) => (
                        <div key={record.id} className="col-12">
                          <div className="card shadow-sm">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1">
                                  <h6 className="fw-bold mb-1" style={{ fontSize: "14px", wordBreak: "break-word" }}>
                                    {record.userEmail || "N/A"}
                                  </h6>
                                  <small className="text-muted">{formatDate(record.date)}</small>
                                </div>
                                <div>{getAttendanceStatus(record)}</div>
                              </div>

                              <hr className="my-2" />

                              <div className="row g-2 mb-2">
                                <div className="col-6">
                                  <small className="text-muted d-block">Login</small>
                                  <strong style={{ fontSize: "13px" }}>{formatTime(record.loginTime)}</strong>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">Logout</small>
                                  <strong style={{ fontSize: "13px" }}>{formatTime(record.logoutTime)}</strong>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">Break Time</small>
                                  <strong style={{ fontSize: "13px" }}>{calculateTotalBreakTime(record.breaks)}</strong>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">Working Hours</small>
                                  <strong style={{ fontSize: "13px" }}>{formatWorkHours(record.totalWorkMinutes)}</strong>
                                </div>
                              </div>

                              {/* Regularization Section for Mobile */}
                              {record.regularization?.status === "approved" && (
                                <div className="mt-2">
                                  <span className="badge bg-success">Regularised</span>
                                </div>
                              )}
                              {record.regularization?.status === "pending" && (
                                <div className="mt-2">
                                  {userRole === "admin" ? (
                                    <button
                                      className="btn btn-sm btn-warning w-100"
                                      onClick={() => setRegularizationModal({
                                        recordId: record.id,
                                        userName: (record.userName || record.userEmail?.split("@")[0] || "User").split(/[._]/)[0],
                                        reason: record.regularization.reason,
                                        date: record.date
                                      })}
                                    >
                                      📋 View Request
                                    </button>
                                  ) : (
                                    <span className="badge bg-warning">Regularization Pending</span>
                                  )}
                                </div>
                              )}
                              {record.regularization?.status === "rejected" && (
                                <div className="mt-2">
                                  <span className="badge bg-danger">Regularization Rejected</span>
                                </div>
                              )}
                              {canRequestRegularization(record) && (
                                <div className="mt-2">
                                  {regularizationInputs[record.id]?.show ? (
                                    <div className="p-2 bg-light rounded">
                                      <textarea
                                        value={regularizationInputs[record.id]?.reason || ""}
                                        onChange={(e) => setRegularizationInputs(prev => ({
                                          ...prev,
                                          [record.id]: { ...prev[record.id], reason: e.target.value }
                                        }))}
                                        placeholder="Enter reason for regularization..."
                                        className="form-control mb-2"
                                        style={{ fontSize: "12px", minHeight: "60px" }}
                                      />
                                      <div className="d-flex gap-2">
                                        <button
                                          className="btn btn-sm btn-primary flex-grow-1"
                                          onClick={() => handleRegularizationRequest(record.id)}
                                          disabled={submittingRegularization === record.id}
                                          style={{ fontSize: "12px" }}
                                        >
                                          {submittingRegularization === record.id ? "Submitting..." : "Submit Request"}
                                        </button>
                                        <button
                                          className="btn btn-sm btn-secondary"
                                          onClick={() => setRegularizationInputs(prev => ({
                                            ...prev,
                                            [record.id]: { show: false, reason: "" }
                                          }))}
                                          style={{ fontSize: "12px" }}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-warning w-100"
                                      onClick={() => setRegularizationInputs(prev => ({
                                        ...prev,
                                        [record.id]: { show: true, reason: "" }
                                      }))}
                                      style={{ fontSize: "12px" }}
                                    >
                                      📝 Request Regularization
                                    </button>
                                  )}
                                </div>
                              )}

                              {record.breaks && record.breaks.length > 0 && (
                                <div className="mt-2">
                                  <button
                                    className="btn btn-sm btn-outline-primary w-100"
                                    onClick={() => toggleRowExpansion(record.id)}
                                    style={{ fontSize: "12px" }}
                                  >
                                    {expandedRows.has(record.id) ? "Hide" : "Show"} Breaks ({record.breaks.length})
                                  </button>
                                  {expandedRows.has(record.id) && (
                                    <div className="mt-2 p-2 bg-light rounded">
                                      <h6 className="fw-bold mb-2" style={{ fontSize: "13px" }}>Break Details:</h6>
                                      {record.breaks.map((b, index) => (
                                        <div key={index} className="mb-1" style={{ fontSize: "12px" }}>
                                          <span className="badge bg-info me-2">
                                            {b.type === "lunch" ? "Break" : (b.type || "Break")} {index + 1}
                                          </span>
                                          <span>
                                            {formatTime(b.start)} - {formatTime(b.end) || "Active"}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  /* Desktop Table View */
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>User Email</th>
                          <th>Login Time</th>
                          <th>Logout Time</th>
                          <th>Break Time</th>
                          <th>Total Working Hours</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Regularization</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedAttendanceRecords.length === 0 ? (
                          <tr>
                            <td colSpan={userRole === "admin" ? "8" : "7"} className="text-center py-4 text-muted">
                              No attendance records found for this date.
                            </td>
                          </tr>
                        ) : (
                          displayedAttendanceRecords.map((record) => (
                            <React.Fragment key={record.id}>
                              <tr>
                                <td style={{ wordBreak: "break-word", maxWidth: "200px" }}>{record.userEmail || "N/A"}</td>
                                <td>{formatTime(record.loginTime)}</td>
                                <td>{formatTime(record.logoutTime)}</td>
                                <td>{calculateTotalBreakTime(record.breaks)}</td>
                                <td>{formatWorkHours(record.totalWorkMinutes)}</td>
                                <td>{formatDate(record.date)}</td>
                                <td>{getAttendanceStatus(record)}</td>
                                <td>
                                  {record.regularization?.status === "approved" ? (
                                    <span className="badge bg-success">Regularised</span>
                                  ) : record.regularization?.status === "pending" ? (
                                    userRole === "admin" ? (
                                      <button
                                        className="btn btn-sm btn-warning"
                                        onClick={() => setRegularizationModal({
                                          recordId: record.id,
                                          userName: (record.userName || record.userEmail?.split("@")[0] || "User").split(/[._]/)[0],
                                          reason: record.regularization.reason,
                                          date: record.date
                                        })}
                                      >
                                        View Request
                                      </button>
                                    ) : (
                                      <span className="badge bg-warning">Pending</span>
                                    )
                                  ) : record.regularization?.status === "rejected" ? (
                                    <span className="badge bg-danger">Rejected</span>
                                  ) : canRequestRegularization(record) ? (
                                    regularizationInputs[record.id]?.show ? (
                                      <div style={{ minWidth: "200px" }}>
                                        <textarea
                                          value={regularizationInputs[record.id]?.reason || ""}
                                          onChange={(e) => setRegularizationInputs(prev => ({
                                            ...prev,
                                            [record.id]: { ...prev[record.id], reason: e.target.value }
                                          }))}
                                          placeholder="Enter reason..."
                                          style={{
                                            width: "100%",
                                            padding: "6px",
                                            borderRadius: "4px",
                                            border: "1px solid #e2e8f0",
                                            fontSize: "12px",
                                            minHeight: "50px",
                                            marginBottom: "6px"
                                          }}
                                        />
                                        <div className="d-flex gap-1">
                                          <button
                                            className="btn btn-sm btn-primary"
                                            onClick={() => handleRegularizationRequest(record.id)}
                                            disabled={submittingRegularization === record.id}
                                            style={{ fontSize: "11px" }}
                                          >
                                            {submittingRegularization === record.id ? "..." : "Submit"}
                                          </button>
                                          <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => setRegularizationInputs(prev => ({
                                              ...prev,
                                              [record.id]: { show: false, reason: "" }
                                            }))}
                                            style={{ fontSize: "11px" }}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        className="btn btn-sm btn-outline-warning"
                                        onClick={() => setRegularizationInputs(prev => ({
                                          ...prev,
                                          [record.id]: { show: true, reason: "" }
                                        }))}
                                        style={{ fontSize: "11px" }}
                                      >
                                        Request
                                      </button>
                                    )
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  {record.breaks && record.breaks.length > 0 && (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => toggleRowExpansion(record.id)}
                                    >
                                      {expandedRows.has(record.id) ? "Hide" : "Show"} Breaks
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {expandedRows.has(record.id) && record.breaks && record.breaks.length > 0 && (
                                <tr>
                                  <td colSpan="9">
                                    <div className="p-3 bg-light rounded">
                                      <h6 className="fw-bold mb-2">Break Details:</h6>
                                      {record.breaks.map((b, index) => (
                                        <div key={index} className="mb-2">
                                          <span className="badge bg-info me-2">
                                            {b.type === "lunch" ? "Break" : (b.type || "Break")} {index + 1}
                                          </span>
                                          <span>
                                            {formatTime(b.start)} - {formatTime(b.end) || "Active"}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>


      {/* ADD USER MODAL */}
      {showUserModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center modal-overlay-responsive"
          style={{ background: "rgba(0,0,0,0.6)", zIndex: 10000, padding: "15px" }}
        >
          <div
            className="bg-white p-4 p-md-5 rounded shadow-lg modal-content-responsive"
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "12px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.15)"
            }}
          >
            {/* Modal Header */}
            <div className="mb-4 d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center me-3"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  backgroundColor: "#eff6ff",
                  color: "#0ea5e9"
                }}
              >
                <i className="bi bi-person-plus-fill" style={{ fontSize: "20px" }}></i>
              </div>
              <h4 className="mb-0" style={{ fontWeight: "600", color: "#1e293b" }}>Add New User</h4>
            </div>

            {/* Username Input */}
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                Username
              </label>
              <div className="input-group">
                <span
                  className="input-group-text"
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#64748b"
                  }}
                >
                  <i className="bi bi-person"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  style={{
                    border: "1px solid #e2e8f0",
                    fontSize: "14px"
                  }}
                />
              </div>
            </div>

            {/* Email Input */}
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                Email Address
              </label>
              <div className="input-group">
                <span
                  className="input-group-text"
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#64748b"
                  }}
                >
                  <i className="bi bi-envelope"></i>
                </span>
                <input
                  type="email"
                  className="form-control"
                  placeholder="user@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  style={{
                    border: "1px solid #e2e8f0",
                    fontSize: "14px"
                  }}
                />
              </div>
            </div>

            {/* Role Select */}
            <div className="mb-4">
              <label className="form-label" style={{ fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                User Role
              </label>
              <div className="input-group">
                <span
                  className="input-group-text"
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#64748b"
                  }}
                >
                  <i className="bi bi-shield-check"></i>
                </span>
                <select
                  className="form-control"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  style={{
                    border: "1px solid #e2e8f0",
                    fontSize: "14px"
                  }}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
              <button
                className="btn modal-btn-responsive"
                onClick={() => {
                  setShowUserModal(false);
                  setNewUsername("");
                  setNewEmail("");
                  setNewRole("user");
                  setNewUserInput("");
                }}
                style={{
                  backgroundColor: "#f1f5f9",
                  color: "#475569",
                  border: "1px solid #e2e8f0",
                  padding: "10px 24px",
                  fontSize: "14px",
                  fontWeight: "500",
                  borderRadius: "8px",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e2e8f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f1f5f9";
                }}
              >
                Cancel
              </button>

              <button
                className="btn modal-btn-responsive"
                onClick={addNewUser}
                style={{
                  backgroundColor: "#0ea5e9",
                  color: "white",
                  border: "none",
                  padding: "10px 24px",
                  fontSize: "14px",
                  fontWeight: "500",
                  borderRadius: "8px",
                  boxShadow: "0 2px 4px rgba(14, 165, 233, 0.2)",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#0284c7";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(14, 165, 233, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#0ea5e9";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(14, 165, 233, 0.2)";
                }}
              >
                <i className="bi bi-plus-circle me-2"></i>
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ASSIGN MODAL */}
      {showAssignModal && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center modal-overlay-responsive" style={{ background: "rgba(0,0,0,0.5)", zIndex: 99999, padding: "15px" }}>
          <div className="bg-white p-3 p-md-4 rounded shadow modal-content-responsive" style={{ width: "100%", maxWidth: "380px" }}>
            <h4 className="mb-2 mb-md-3 modal-title-responsive">Assign Users</h4>

            <div className="assign-users-list" style={{ maxHeight: "200px", overflowY: "auto" }}>
              {allUsers
                .filter((u) => u.role !== "admin")
                .map((u) => (
                  <div key={u.id} className="d-flex align-items-center mb-2">
                    <input type="checkbox" checked={assignedUsers.includes(u.id)} onChange={() => toggleUser(u.id)} />
                    <span className="ms-2 assign-user-text">{u.username || u.email}</span>
                  </div>
                ))}
            </div>

            <div className="text-end mt-3 d-flex flex-column flex-sm-row justify-content-end gap-2">
              <button className="btn btn-secondary modal-btn-responsive" onClick={() => setShowAssignModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary modal-btn-responsive" onClick={saveAssignedUsers}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Panel - Removed bell icon, using badges on project cards only */}

      {/* REGULARIZATION APPROVAL MODAL (Admin) */}
      {regularizationModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 10001
          }}
          onClick={(e) => e.target === e.currentTarget && setRegularizationModal(null)}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "340px" }}>
            <div className="modal-content" style={{ borderRadius: "8px" }}>
              <div className="modal-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold" style={{ fontSize: "14px" }}>
                    {regularizationModal.userName.charAt(0).toUpperCase() + regularizationModal.userName.slice(1)}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "12px" }}>{formatDate(regularizationModal.date)}</span>
                    <button
                      type="button"
                      className="btn-close"
                      style={{ fontSize: "10px" }}
                      onClick={() => setRegularizationModal(null)}
                    ></button>
                  </div>
                </div>
                <div
                  className="p-2 rounded mb-3"
                  style={{ background: "#f8f9fa", fontSize: "13px", color: "#374151" }}
                >
                  {regularizationModal.reason}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-danger flex-grow-1"
                    onClick={() => {
                      handleRejectRegularization(regularizationModal.recordId);
                      setRegularizationModal(null);
                    }}
                  >
                    Reject
                  </button>
                  <button
                    className="btn btn-sm btn-success flex-grow-1"
                    onClick={() => {
                      handleApproveRegularization(regularizationModal.recordId);
                      setRegularizationModal(null);
                    }}
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ATTENDANCE MODAL */}
      <AttendanceModal
        show={showAttendanceModal}
        onClose={() => setShowAttendanceModal(false)}
        userEmail={userEmail}
        userId={userUID}
        userName={userName}
      />
    </div>
  );
}
